/**
 * photo-frame / index.js — bootstrap and UI wiring.
 *
 * The one behavioural rule this page is built around: the moment a photo
 * decodes it is ON the canvas, inside a good-looking default frame, with every
 * control live. `uploading` — "picked a file, then nothing" — is the top
 * measured abandon stage on every photo tool we run (photo-editor/index.js
 * documents the numbers), and a frame tool has even less excuse for a dead
 * moment: the первое впечатление IS the product.
 *
 * Shell rebuilt 2026-08-26 around two complaints that were really one:
 * «я не заметил этот select» and «неудобно управлять картинкой и размером».
 * Both came from the same mistake — the controls a person reaches for FIRST
 * (how the photo sits in the frame, what shape the card is) were the ones
 * parked behind a tab or flattened into an unlabelled full-width <select>.
 * They now live in the two places the eye already is: a labelled pill in the
 * top bar, and a strip directly under the canvas. Export moved out of the tab
 * strip entirely, into its own block below the editor.
 *
 * The doc/render split lives in doc.js; frames in frames.js. This file owns
 * DOM, gestures, undo, the galleries, and export.
 *
 * Since 2026-11-04 several photos at once make a PACK (pack.js): one design,
 * a card per photo, each photo placed by hand, all of them downloaded as one
 * archive. The current photo of the pack is mirrored into slot 0 of the card
 * (doc.photos[0] IS its slot object), so everything below that edits «the
 * photo in the frame» edits the current photo of the pack unchanged.
 */

'use strict';

import { decodeFile, release, looksLikeImage } from '../merge-images/decode.js';
import { formatSupported, mimeFor, copyToClipboard } from '../merge-images/export.js';
import { canvasLimits } from '../canvas-limits.js';
import {
    newDoc, newSlot, newText, newSticker, render, docAspect, photoWindows,
    hitTest, selectionHandle, ASPECTS,
} from './doc.js';
import { framesForLocale, catsForLocale, frameById, frameHasAlpha } from './frames.js';
import { fontsForLocale, cssFamily, loadFont, warmAll, ensureDocFonts, missingDocFonts } from './fonts.js';
import { templatesForLocale } from './templates.js';
import {
    PACK_MAX, pendingItem, readyItem, ingest, proxyAsset, freeDecoded, slotTouched, cloneSlot, FullDecodes,
} from './pack.js';

const SLUG = 'photo-frame';
const CFG = window.PF_CFG || {};
const I18N = window.PF_I18N || {};
const T = (k) => (I18N[k] != null ? I18N[k] : k);
const LOCALE = (window.__LOCALE || 'en').split('-')[0];
const RTL = LOCALE === 'ar';

const $ = (id) => document.getElementById(id);
const toast = (m, t) => { try { window.__toast ? window.__toast(m, t) : console.log('[toast]', t, m); } catch (_) {} };
const announce = (m) => { try { window.__announce && window.__announce(m); } catch (_) {} };
const stage = (s, d) => { try { window.__stage && window.__stage(s, d); } catch (_) {} };
const funnel = (s, d) => { try { window.__aeFunnel && window.__aeFunnel(s, d); } catch (_) {} };
const settings = {
    save(k, v) { try { window.__toolSettings && window.__toolSettings.save(SLUG, k, v); } catch (_) {} },
    load(k, f) { try { return window.__toolSettings ? window.__toolSettings.load(SLUG, k, f) : f; } catch (_) { return f; } },
};

/* ─── state ───────────────────────────────────────────────────────────────── */

let doc = newDoc();
let assets = { photos: [null, null], customFrame: null };
let selection = null;               // {kind:'text'|'sticker', id}
let activeSlot = 0;
let dirty = false;                  // composed something, not yet exported
let editingStarted = false;

const undoStack = [];
const redoStack = [];
let undoScheduled = null;

let cv, ctx, stageEl;
let previewW = 0, previewH = 0;
let rafPending = false;
let hoverFrame = null;              // рамка под курсором в галерее — живая примерка
let pendingTpl = null;              // ?tpl=morning — диплинк на шаблон, применяется к первому фото

/* The bytes each picture on the card came from, beside its decoded bitmap in
   `assets`: what «Мои работы» keeps, so a card that comes back goes through the
   same decode chain as a fresh pick (EXIF, HEIC), not through a stored canvas. */
let SRC = { photos: [null, null], frame: null };
let picking = 0;                    // photos being decoded right now (a pick in flight)

/* The pack (pack.js): null = one card; {items, cur} = several photos under this
   design. Full decodes of its photos live in `fulls` (a 3-photo LRU), the rest
   as proxies; `packGen` bumps whenever the pack is replaced or dropped, and an
   ingest of the old one stops at its next step. */
let pack = null;
const fulls = new FullDecodes(3);
let packGen = 0;
let packDefaults = null;            // the slot «Ко всем» last set — what a photo added later starts from
let fontEpoch = 0;                  // a caption face arrived: the cards in the ribbon are stale

/* ─── boot ────────────────────────────────────────────────────────────────── */

document.addEventListener('DOMContentLoaded', init);

function init() {
    cv = $('pfCanvas');
    if (!cv) return;
    ctx = cv.getContext('2d');
    stageEl = $('pfStage');

    doc._locale = LOCALE;
    doc.aspect = settings.load('aspect', 'auto');

    buildAspectMenu();
    bindLayoutSeg();
    bindSlotSeg();
    buildCats();
    buildFrameGallery();
    buildTintRow();
    bindFrameOpts();
    buildFontGrid();
    buildTextStyles();
    bindTabs();
    bindCtxBar();
    bindTextPanel();
    bindPhotoPanel();
    bindStickerPanel();
    bindExportPanel();
    bindCanvasGestures();
    bindKeyboard();
    bindUploader();
    bindStageDrop();
    bindPaste();
    bindCustomFrame();
    bindAiFrameDoor();
    bindSecondPhoto();
    bindPack();
    syncAspectPill();
    syncTintRow();

    pendingTpl = new URLSearchParams(location.search).get('tpl');
    const wantCat = new URLSearchParams(location.search).get('cat');
    if (wantCat) currentCat = wantCat;

    window.addEventListener('resize', () => { if (assets.photos[0]) layoutCanvas(); });
    window.addEventListener('beforeunload', (e) => {
        // Losing twenty minutes of card-making to a stray click is the top
        // complaint against mycollages — guard the tab, not the user.
        // Since 30.09.2026 «Мои работы» keeps the card, and leaving no longer
        // loses it: the next visit brings it back. So the question is asked
        // only where it still guards something — no store in this browser,
        // nothing saved yet, or the last save failed (workKept()).
        if (dirty && !workKept()) { e.preventDefault(); e.returnValue = ''; }
    });

    stage('ready');

    // Test/debug handle (photo-editor exposes __PE the same way): the deep
    // playwright suite asserts on real state instead of screen-scraping.
    window.__PF = {
        get doc() { return doc; },
        get assets() { return assets; },
        get src() { return SRC; },
        get selection() { return selection; },
        rerender: requestRender,
        exportDims,
        handle: () => selectionHandle(ctx, previewW, previewH, doc, selection),
        get previewSize() { return { w: previewW, h: previewH }; },
        get pack() { return pack; },
        get fullsHeld() { return fulls.map.size; },
        packGo: (i) => packGo(i),
    };

    draftsBoot();
}

/* live — a slider or a drag in motion: saved as it moves, counted as a step only
   when it is let go (draftTouch(true) on release). */
function markEdit(live) {
    dirty = true;
    if (!editingStarted) { editingStarted = true; stage('editing'); }
    draftTouch(!live);
}

/* ─── undo ────────────────────────────────────────────────────────────────── */

/* The card as a saved work sees it: the design plus the slot on the stage. */
function designJson() { const { _locale, ...rest } = doc; return JSON.stringify(rest); }

/* An undo step. In a pack it also carries every photo's placement and which
   photo was on the stage: «Ко всем» changes all of them in one step, and an undo
   after paging on must bring back the photo it changes, not paint its crop onto
   the photo now on the stage. `curId` — the photo the step is ABOUT, when it is
   not the one on the stage now (the mirror step undo/redo leave behind). */
function snapshot(curId) {
    if (!pack) return designJson();
    const { _locale, ...rest } = doc;
    const slots = {};
    for (const it of pack.items) slots[it.id] = it.slot;
    const cur = curItem();
    return JSON.stringify(Object.assign({}, rest, { _pk: { cur: curId || (cur ? cur.id : null), slots } }));
}
/* Which photo a step was taken on (null outside a pack). */
function stepPhoto(json) {
    try { const pk = JSON.parse(json)._pk; return pk && pk.cur ? pk.cur : null; } catch (_) { return null; }
}
function pushUndo() {
    // Slider drags mutate per-frame; collapse them into one step per gesture.
    if (undoScheduled) return;
    const snap = snapshot();
    undoScheduled = setTimeout(() => { undoScheduled = null; }, 350);
    if (undoStack[undoStack.length - 1] === snap) return;
    undoStack.push(snap);
    if (undoStack.length > 60) undoStack.shift();
    redoStack.length = 0;
    updateUndoButtons();
}
function restore(json) {
    const loc = doc._locale;
    const keepPhotos = doc.photos;
    const next = JSON.parse(json);
    const pk = next._pk;
    delete next._pk;
    doc = next;
    doc._locale = loc;
    selection = null;
    if (pack) {
        // The placements come back by photo; a step taken before the pack existed
        // brings back the design only.
        if (pk && pk.slots) for (const it of pack.items) if (pk.slots[it.id]) it.slot = pk.slots[it.id];
        doc.layout = 'single';
        let i = pk && pk.cur ? pack.items.findIndex((it) => it.id === pk.cur) : -1;
        if (i < 0 || pack.items[i].state !== 'ready') i = pack.cur;
        packGo(i, { quiet: true });
        syncPanelsFromDoc();
        scheduleStrip();
        return;
    }
    // A step taken inside a pack that has since shrunk to one card: its slot
    // belonged to whichever photo was on the stage then — keep this photo's.
    if (pk) doc.photos = keepPhotos;
    syncPanelsFromDoc();
    layoutCanvas();
}
function undo() {
    if (!undoStack.length) return;
    // The step being undone happened on ITS photo; redoing it must show that
    // photo again, not the one the person happened to be on when pressing undo.
    const step = undoStack.pop();
    redoStack.push(snapshot(stepPhoto(step)));
    restore(step);
    updateUndoButtons();
    markEdit();                          // the card changed — the saved one follows
}
function redo() {
    if (!redoStack.length) return;
    const step = redoStack.pop();
    undoStack.push(snapshot(stepPhoto(step)));
    restore(step);
    updateUndoButtons();
    markEdit();
}
function updateUndoButtons() {
    $('pfUndo').disabled = !undoStack.length;
    $('pfRedo').disabled = !redoStack.length;
}

/* ─── uploader ────────────────────────────────────────────────────────────── */

function bindUploader() {
    const zone = $('pfDrop');
    if (!zone) return;
    const handler = async (file, detail) => {
        const files = detail && detail.files && detail.files.length ? detail.files : [file];
        await openFiles(files, 'pick');
    };
    if (window.__bindUploaderFile) window.__bindUploaderFile(zone, handler);
    else zone.addEventListener('tool-uploader:file', (e) => handler(e.detail.file, e.detail));
    zone.addEventListener('tool-uploader:clear', startOver);
    $('pfUndo').addEventListener('click', undo);
    $('pfRedo').addEventListener('click', redo);
}

/** Dropping straight onto the canvas is what people try first once a photo is
 *  already there — the collapsed uploader row is not where the eye is. */
function bindStageDrop() {
    const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
    stageEl.addEventListener('dragover', (e) => { stop(e); stageEl.classList.add('is-drop'); });
    stageEl.addEventListener('dragleave', () => stageEl.classList.remove('is-drop'));
    stageEl.addEventListener('drop', async (e) => {
        stop(e);
        stageEl.classList.remove('is-drop');
        const files = [...(e.dataTransfer && e.dataTransfer.files ? e.dataTransfer.files : [])]
            .filter((f) => /^image\//.test(f.type) || /\.(heic|heif)$/i.test(f.name));
        if (files.length) { funnel('drop_on_stage', { n: files.length }); await openFiles(files, 'add'); }
    });
}

/** Ctrl+V with a screenshot in the clipboard. Free on every Chromium and
 *  Safari 16+; a paste with no image simply falls through to the page. */
function bindPaste() {
    document.addEventListener('paste', async (e) => {
        const tag = (e.target && e.target.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || (e.target && e.target.isContentEditable)) return;
        const items = e.clipboardData && e.clipboardData.files ? [...e.clipboardData.files] : [];
        const imgs = items.filter((f) => /^image\//.test(f.type));
        if (!imgs.length) return;
        e.preventDefault();
        funnel('paste_photo', {});
        await openFiles(imgs, 'add');
    });
}

/* A pick in flight counts as «the person has started» (draft started()): an
   unfinished card from before must not land on top of a photo still decoding.
   how: 'pick' — the uploader zone, its files ARE the new selection;
        'add'  — a drop on the canvas, a paste, «＋ Добавить фото»: more of the same. */
async function openFiles(files, how) {
    picking++;
    try { await placeFiles([...files], how || 'pick'); } finally { picking--; }
}

/* The bytes a picture came from, as a File that lives in memory — what a saved
   card keeps. Taken at once: a picked File can go stale while it waits (iOS
   releases the reference — merge-images/decode.js), and the save comes later. */
async function ownBytes(file) {
    if (!file || !looksLikeImage(file)) return file;
    try {
        const buf = await file.arrayBuffer();
        return new File([buf], file.name || 'image', { type: file.type || '', lastModified: file.lastModified || Date.now() });
    } catch (_) { return file; }
}

async function placeFiles(files, how) {
    funnel('file_select', { n: files.length });
    // Several photos at once are a pack: one design, a card per photo. Until
    // 2026-11-04 they went into a two-window card and every photo past the
    // second was dropped without a word. The one exception left: a card already
    // switched to two windows, picking exactly two.
    if (pack) {
        if (how === 'add') { await addToPack(files); return; }
        if (files.length > 1) { await startPack(files, { keepCurrent: false }); return; }
        leavePack(null);                 // the zone picked ONE photo: a single card again
    } else if (files.length > 1 && !(doc.layout !== 'single' && files.length === 2)) {
        await startPack(files, { keepCurrent: how === 'add' && !!assets.photos[0] });
        return;
    }
    const targets = [];
    if (!assets.photos[0]) targets.push(0);
    if (files.length > 1 || (doc.layout !== 'single' && !assets.photos[1])) targets.push(1);
    // Both slots taken and the user picked again («Другой файл» on the collapsed
    // zone): this is a REPLACE, not a no-op — the first suite run proved the
    // silent-ignore variant ships a dead re-pick button.
    if (!targets.length) {
        // Single layout shows only slot 0 — replacing a hidden slot would look
        // like the pick did nothing.
        const t = doc.layout === 'single' ? 0 : activeSlot;
        targets.push(t);
        if (files.length > 1) targets.push(t === 0 ? 1 : 0);
    }

    let loadedAny = false;
    for (let i = 0; i < targets.length && i < files.length; i++) {
        const slotIdx = targets[i];
        try {
            const src = await ownBytes(files[i]);
            const dec = await decodeFile(src, { emit: (n, d) => funnel(n, d) });
            if (assets.photos[slotIdx]) release(assets.photos[slotIdx]);
            assets.photos[slotIdx] = dec;
            SRC.photos[slotIdx] = src;
            doc.photos[slotIdx] = newSlot();
            loadedAny = true;
        } catch (err) {
            const code = (err && err.code) || 'IMAGE_DECODE_FAILED';
            funnel('decode_failed', { code });
            toast(T(code === 'NOT_IMAGE' ? 'err.not_image' : code === 'HEIC_FAILED' ? 'err.heic' : 'err.decode'), 'error');
        }
    }
    if (!loadedAny) return;
    editorOpened(T('msg.loaded'));
}

/* The first photo is on the canvas: open the editor around it. */
function editorOpened(said) {
    const app = $('pfApp');
    app.hidden = false;
    app.classList.add('is-editing');
    layoutCanvas();
    // Instant first impression: the default frame is already applied — the
    // user sees a finished-looking card before touching a single control.
    markEdit();
    syncPanelsFromDoc();
    updateExpEstimate();
    announce(said);
    if (pendingTpl) {
        // Диплинк ?tpl=morning: закладка «моё доброе утро» открывает страницу,
        // и первое же фото сразу одевается в шаблон.
        const id = pendingTpl;
        pendingTpl = null;
        applyTemplate(id);
        funnel('tpl_deeplink', { tpl: id });
    } else {
        openTab('frames');
    }
}

/* The page as a new visit finds it. Since «Мои работы» (30.09.2026) this is
   also the controller's «new work», so it owns everything a card leaves behind:
   the own-frame bitmap and chip, the slot the controls point at, and the canvas
   shape — a new page takes the one the person last chose, not 'auto'. */
function resetAll() {
    for (const p of assets.photos) if (p) release(p);
    if (assets.customFrame) release(assets.customFrame);
    dropPack();
    assets = { photos: [null, null], customFrame: null };
    SRC = { photos: [null, null], frame: null };
    doc = newDoc();
    doc._locale = LOCALE;
    doc.aspect = settings.load('aspect', 'auto');
    selection = null;
    activeSlot = 0;
    hoverFrame = null;
    dirty = false;
    undoStack.length = 0; redoStack.length = 0;
    const app = $('pfApp');
    app.hidden = true;
    app.classList.remove('is-editing');
    syncPanelsFromDoc();
    syncFrameUi();
}

/* × on the picked-file row: start over. With «Мои работы» the card on the page
   stays in the list, so — as before — nothing is asked; where no card can be
   kept this is the old reset, still without a question. */
function startOver() {
    if (freshening) return;              // draftFresh() itself put the zone back
    if (drafts && drafts.store()) drafts.reset();
    else resetAll();
}

/* ─── canvas layout + render ──────────────────────────────────────────────── */

function layoutCanvas() {
    // The stage is a fixed-height box now (the editor has to fit one screen),
    // so the canvas is fitted INTO it on both axes instead of only width.
    const cs = getComputedStyle(stageEl);
    const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
    const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const boxW = Math.max(120, stageEl.clientWidth - padX);
    const boxH = Math.max(120, stageEl.clientHeight - padY);
    const ar = docAspect(doc, assets);
    let w = boxW, h = boxW / ar;
    if (h > boxH) { h = boxH; w = h * ar; }
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    previewW = Math.round(w * dpr);
    previewH = Math.round(h * dpr);
    cv.width = previewW; cv.height = previewH;
    cv.style.width = `${Math.round(w)}px`;
    cv.style.height = `${Math.round(h)}px`;
    requestRender();
    updateExpEstimate();
}

function requestRender() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => {
        rafPending = false;
        // Hover try-on: пока курсор ведёт по ленте рамок, холст меряет каждую —
        // без записи в документ и без undo-шагов.
        const d = hoverFrame ? Object.assign({}, doc, { frame: hoverFrame }) : doc;
        render(ctx, previewW, previewH, d, assets, { selection });
        // Every change of the card reaches the canvas through here — the ribbon
        // of the pack follows (try-on excluded: it is not a change).
        if (pack && !hoverFrame) scheduleStrip();
    });
}

/* A caption face finished loading: the canvas and the cards in the ribbon were
   drawn with the fallback. */
function fontLoaded() { fontEpoch++; requestRender(); }

/* ─── the pack: several photos, one design ────────────────────────────────────
   pack.js holds the model and the memory rules; this part opens, fills, pages
   and empties the pack, and draws its ribbon. */

function curItem() { return pack ? pack.items[pack.cur] || null : null; }
function packLoading() { return !!pack && pack.items.some((it) => it.state === 'loading'); }
function packCount() { return pack ? pack.items.length : 0; }

/* The card of one photo of the pack: the shared design over THAT photo's slot. */
function docFor(it) {
    return Object.assign({}, doc, { photos: [it.slot, doc.photos[1]], layout: 'single' });
}

/** Turn the page into a pack. keepCurrent: the photo already on the card becomes
 *  the first one (a drop, a paste or «＋» while a card is open), its crop kept. */
async function startPack(files, opts) {
    const gen = ++packGen;
    const kept = [];
    if (opts && opts.keepCurrent) {
        const n = doc.layout === 'single' ? 1 : 2;
        for (let i = 0; i < n; i++) {
            const dec = assets.photos[i];
            if (!dec || !SRC.photos[i] || dec.proxy) continue;
            kept.push([readyItem(SRC.photos[i], dec, doc.photos[i]), dec]);
        }
    }
    fulls.clear();
    for (const p of assets.photos) if (p && !kept.some(([, d]) => d === p)) release(p);
    assets.photos = [null, null];
    SRC.photos = [null, null];
    pack = { items: kept.map(([it]) => it), cur: 0 };
    for (const [it, dec] of kept) fulls.put(it, dec);
    doc.layout = 'single';
    doc.photos[1] = newSlot();
    activeSlot = 0;
    buildStrip();
    if (pack.items.length) packGo(0, { quiet: true });
    syncPackUi();
    funnel('pack_start', { n: files.length + kept.length, kept: kept.length });
    await addToPack(files, gen);
}

/** More photos into the pack. Each one shows up in the ribbon at once as a
 *  placeholder and becomes a card when it has decoded — one at a time, so a
 *  pack of 80 never holds more than one full decode in flight. */
async function addToPack(files, gen0) {
    if (!pack) return;
    const gen = gen0 || packGen;
    let list = [...files];
    const room = PACK_MAX - pack.items.length;
    if (list.length > room) {
        toast(T('warn.pack_cap').replace(':max', PACK_MAX), 'warning');
        list = list.slice(0, Math.max(0, room));
    }
    if (!list.length) { settlePack(); return; }
    const fresh = list.map((f) => pendingItem(f, cloneSlot(packDefaults)));
    pack.items.push(...fresh);
    const track = $('pfStripTrack');
    for (const it of fresh) track.appendChild(shotEl(it));
    syncStripSel();
    syncPackUi();
    if (!gen0) funnel('pack_add', { n: fresh.length, total: pack.items.length });
    const failed = await ingestItems(fresh, gen);
    if (gen !== packGen || !pack) return;
    if (failed.length) {
        const names = failed.slice(0, 3).join(', ') + (failed.length > 3 ? ` +${failed.length - 3}` : '');
        toast(T('warn.pack_failed').replace(':names', names), 'warning');
    }
    settlePack();
}

/* Decode the placeholders in order. The first photo to arrive goes on the
   stage (and opens the editor if nothing was open); the rest keep only their
   proxy. → the names of the files that could not be opened (they leave the
   ribbon). */
async function ingestItems(list, gen) {
    const failed = [];
    for (const it of list) {
        if (gen !== packGen || !pack) return failed;
        if (!pack.items.includes(it)) continue;              // removed while it waited
        try {
            const dec = await ingest(it, (n, d) => funnel(n, d));
            if (gen !== packGen || !pack || !pack.items.includes(it)) { freeDecoded(dec); continue; }
            // ⚠️ ingest() has already marked `it` ready: «is the current item
            // ready» cannot tell the first arrival from the rest — the stage can.
            if (!assets.photos[0] || curItem() === it) {
                fulls.put(it, dec);
                packGo(pack.items.indexOf(it), { quiet: true });
                if ($('pfApp').hidden) editorOpened(T('msg.pack_loaded'));
            } else {
                freeDecoded(dec);
            }
            shotReady(it);
        } catch (err) {
            funnel('decode_failed', { code: (err && err.code) || 'IMAGE_DECODE_FAILED' });
            failed.push(it.name);
            removeItem(it, { quiet: true });
        }
        syncPackUi();
        if (window.__yieldTick) await window.__yieldTick(0);
    }
    return failed;
}

/* Everything has decoded: a pack of one is a card again, the zone names the
   photos, and the pack is saved as part of the work. */
function settlePack(opts) {
    if (!pack || packLoading()) return;
    if (!pack.items.length) {
        leavePack(null);
        if (!assets.photos[0]) {
            $('pfApp').hidden = true;
            try { const z = $('pfDrop'); if (z && z.__uploaderRestore) z.__uploaderRestore(true); } catch (_) {}
        }
        return;
    }
    if (pack.items.length === 1) { leavePack(pack.items[0]); return; }
    try {
        const z = $('pfDrop');
        if (z && z.__uploaderCollapse) z.__uploaderCollapse(pack.items.map((it) => it.file));
    } catch (_) {}
    syncPackUi();
    updateExpEstimate();
    // A restored pack is the work as it was saved, not a new edit of it (the
    // controller writes nothing on a mere open).
    if (!(opts && opts.restoring)) markEdit();
}

/** Back to one card. keep — the photo that stays on it (null: none). */
function leavePack(keep) {
    if (!pack) return;
    packGen++;
    const dec = keep ? fulls.take(keep) : null;
    fulls.clear();
    pack = null;
    packDefaults = null;
    clearStrip();
    if (keep) {
        assets.photos = [dec || proxyAsset(keep), null];
        SRC.photos = [keep.file, null];
        doc.photos = [keep.slot, newSlot()];
        if (!dec) {
            // The proxy stands in until the full photo is back — the export
            // waits for it (stageFull()), the screen just sharpens.
            decodeFile(keep.file, {}).then((full) => {
                if (!pack && SRC.photos[0] === keep.file && assets.photos[0] && assets.photos[0].proxy) {
                    assets.photos[0] = full; requestRender(); updateExpEstimate();
                } else release(full);
            }, () => {});
        }
        try { const z = $('pfDrop'); if (z && z.__uploaderCollapse) z.__uploaderCollapse([keep.file]); } catch (_) {}
    } else {
        assets.photos = [null, null];
        SRC.photos = [null, null];
    }
    activeSlot = 0;
    syncPackUi();
    if (!$('pfApp').hidden) { layoutCanvas(); syncPanelsFromDoc(); }
}

/* «Начать заново» and a new work: the pack goes with everything else. */
function dropPack() {
    packGen++;
    pack = null;
    packDefaults = null;
    fulls.clear();
    clearStrip();
}

/** Put photo i of the pack on the stage. */
function packGo(i, opts) {
    const it = pack && pack.items[i];
    if (!it || it.state !== 'ready') return;
    pack.cur = i;
    doc.photos[0] = it.slot;
    SRC.photos[0] = it.file;
    const full = fulls.get(it);
    assets.photos[0] = full || proxyAsset(it);
    activeSlot = 0;
    hoverFrame = null;
    layoutCanvas();                          // «Как у фото»: every card has its own shape
    syncCtxBar();
    syncPhotoPanel();
    syncStripSel(true);
    updatePackNav();
    if (!(opts && opts.quiet)) announce(T('ui.pack_shot').replace(':i', i + 1).replace(':n', pack.items.length));
    if (!full) {
        fulls.ensure(it, it.id).then((dec) => {
            if (curItem() !== it || !assets.photos[0] || !assets.photos[0].proxy) return;
            assets.photos[0] = dec;
            requestRender();
            updateExpEstimate();
        }, () => {});
    }
    // Paging on is the way through a pack: have the next photo decoded by then.
    const next = pack.items[i + 1];
    if (next && next.state === 'ready' && !fulls.get(next)) {
        setTimeout(() => { if (pack && curItem() === it) fulls.ensure(next, it.id).catch(() => {}); }, 350);
    }
}

function packStep(d) {
    if (!pack) return;
    for (let i = pack.cur + d; i >= 0 && i < pack.items.length; i += d) {
        if (pack.items[i].state === 'ready') { packGo(i); return; }
    }
}

function removeItem(it, opts) {
    if (!pack) return;
    const i = pack.items.indexOf(it);
    if (i < 0) return;
    pack.items.splice(i, 1);
    fulls.drop(it);
    if (it._el) it._el.remove();
    it._el = null;
    if (!(opts && opts.quiet)) funnel('pack_remove', { left: pack.items.length });
    if (i < pack.cur) pack.cur--;
    else if (i === pack.cur) {
        // The neighbour that slid into its place — or the one before, at the end.
        pack.cur = Math.min(i, pack.items.length - 1);
        const ready = pack.items.findIndex((x, k) => k >= pack.cur && x.state === 'ready');
        const back = [...pack.items].reverse().findIndex((x) => x.state === 'ready');
        const j = ready >= 0 ? ready : (back >= 0 ? pack.items.length - 1 - back : -1);
        if (j >= 0) packGo(j, { quiet: true });
    }
    syncStripSel();
    if (!(opts && opts.quiet) && !packLoading()) {
        if (pack.items.length <= 1) { settlePack(); return; }
        markEdit();
        updateExpEstimate();
    }
    syncPackUi();
}

/* «＋ Добавить фото» (top bar and the ribbon's own tile). */
function bindPack() {
    const input = $('pfPackInput');
    const open = () => { funnel('pack_add_open', { pack: pack ? 1 : 0 }); input.click(); };
    $('pfPackAdd').addEventListener('click', open);
    $('pfStripAdd').addEventListener('click', open);
    input.addEventListener('change', async () => {
        const files = input.files ? [...input.files] : [];
        input.value = '';
        if (!files.length) return;
        picking++;
        try {
            if (pack) await addToPack(files);
            else await startPack(files, { keepCurrent: !!assets.photos[0] });
        } finally { picking--; }
    });

    // «Previous» is the photo before in the pack in every script: the button
    // sits at the inline start (the right in Arabic), where the ribbon starts.
    $('pfNavPrev').addEventListener('click', () => { packStep(-1); funnel('pack_nav', { via: 'arrow' }); });
    $('pfNavNext').addEventListener('click', () => { packStep(1); funnel('pack_nav', { via: 'arrow' }); });

    const track = $('pfStripTrack');
    track.addEventListener('click', (e) => {
        const shot = e.target.closest('.pf-shot');
        if (!shot || !pack) return;
        const it = pack.items.find((x) => x.id === shot.dataset.id);
        if (!it) return;
        if (e.target.closest('.pf-shot-del')) { removeItem(it); return; }
        const i = pack.items.indexOf(it);
        if (i !== pack.cur) { packGo(i); funnel('pack_nav', { via: 'ribbon' }); }
    });
    // The ribbon is one tab stop (roving focus): arrows walk it, Home/End jump,
    // Delete takes the photo out. Stopped here, so the document's own arrow and
    // Delete keys (they move and remove the selected caption) never see them.
    track.addEventListener('keydown', (e) => {
        if (!pack) return;
        const fwd = RTL ? 'ArrowLeft' : 'ArrowRight';
        const back = RTL ? 'ArrowRight' : 'ArrowLeft';
        let handled = true;
        if (e.key === fwd) packStep(1);
        else if (e.key === back) packStep(-1);
        else if (e.key === 'Home') packGo(pack.items.findIndex((x) => x.state === 'ready'));
        else if (e.key === 'End') {
            const r = [...pack.items].reverse().findIndex((x) => x.state === 'ready');
            if (r >= 0) packGo(pack.items.length - 1 - r);
        } else if (e.key === 'Delete' || e.key === 'Backspace') {
            const it = curItem();
            if (it) removeItem(it);
        } else handled = false;
        if (!handled) return;
        e.preventDefault();
        e.stopPropagation();
        const b = pack && curItem() && curItem()._el && curItem()._el.querySelector('.pf-shot-pick');
        if (b) b.focus({ preventScroll: true });
    });
    // PageUp / PageDown page through the pack while the editor has the focus —
    // and only when the focused element has no use for the key itself.
    document.addEventListener('keydown', (ev) => {
        if (!pack || ev.defaultPrevented || (ev.key !== 'PageDown' && ev.key !== 'PageUp')) return;
        if (!$('pfApp').contains(document.activeElement)) return;
        if (window.__keyOwnedByFocus && window.__keyOwnedByFocus(ev)) return;
        ev.preventDefault();
        packStep(ev.key === 'PageDown' ? 1 : -1);
    });

    // «Ко всем»: the way this photo lies in the frame (fill / whole) for every
    // photo of the pack, and for the photos added after.
    $('pfFitAll').addEventListener('click', () => {
        if (!pack) return;
        pushUndo();
        const fit = slot().fit || 'fill';
        for (const it of pack.items) {
            if (it.slot.fit === fit) continue;
            it.slot.fit = fit;
            if (fit === 'fit') { it.slot.pan.x = 0; it.slot.pan.y = 0; }
        }
        packDefaults = Object.assign(cloneSlot(packDefaults), { fit });
        markEdit();
        requestRender();
        // The ribbon shows the change on every card; a toast here would sit over the canvas.
        announce(T('msg.pack_applied').replace(':n', pack.items.length));
        funnel('pack_apply_all', { what: 'fit', v: fit, n: pack.items.length });
    });
    // Light and colour of this photo for every photo of the pack.
    $('pfAdjustAll').addEventListener('click', () => {
        if (!pack) return;
        pushUndo();
        const a = slot().adjust;
        for (const it of pack.items) it.slot.adjust = Object.assign({}, a);
        packDefaults = Object.assign(cloneSlot(packDefaults), { adjust: Object.assign({}, a) });
        markEdit();
        requestRender();
        // The ribbon shows the change on every card; a toast here would sit over the canvas.
        announce(T('msg.pack_applied').replace(':n', pack.items.length));
        funnel('pack_apply_all', { what: 'adjust', n: pack.items.length });
    });
}

/* Everything on the page that differs between one card and a pack. */
function syncPackUi() {
    const on = !!pack;
    const n = packCount();
    const app = $('pfApp');
    app.classList.toggle('is-pack', on);
    $('pfStrip').hidden = !on;
    $('pfFitAll').hidden = !on;
    $('pfAdjustAll').hidden = !on;
    $('pfHint').textContent = T(on ? 'ui.pack_hint' : 'ui.stage_hint');
    $('pfOneLab').hidden = !on;
    $('pfDownloadOne').hidden = !on;
    $('pfDownloadLabel').textContent = on ? T('ui.pack_download').replace(':n', n) : T('ui.download');
    $('pfDownload').disabled = on && packLoading();
    updatePackNav();
    syncLayoutSeg();
}

function updatePackNav() {
    const on = !!pack;
    const prev = $('pfNavPrev'), next = $('pfNavNext');
    prev.hidden = next.hidden = !on;
    if (!on) return;
    const readyAt = (d) => {
        for (let i = pack.cur + d; i >= 0 && i < pack.items.length; i += d) if (pack.items[i].state === 'ready') return true;
        return false;
    };
    prev.disabled = !readyAt(-1);
    next.disabled = !readyAt(1);
    $('pfStripCount').textContent = `${pack.cur + 1} / ${pack.items.length}`;
}

/* ─── the ribbon ──────────────────────────────────────────────────────────────
   Every tile is the finished card — this design over that photo, drawn by the
   same render() as the export — so the whole pack can be checked at a glance.
   Tiles repaint only when seen and only when their card changed. */

const SHOT_H = 52;                           // CSS px — keep in step with .pf-shot canvas
let stripObserver = null;
let stripTimer = null;

function buildStrip() {
    clearStrip();
    if (!pack) return;
    const track = $('pfStripTrack');
    for (const it of pack.items) track.appendChild(shotEl(it));
    syncStripSel();
}

function clearStrip() {
    if (stripObserver) stripObserver.disconnect();
    stripObserver = null;
    const track = $('pfStripTrack');
    if (track) track.textContent = '';
}

function observeShot(el) {
    if (!stripObserver) {
        stripObserver = new IntersectionObserver((entries) => {
            let any = false;
            for (const en of entries) {
                const it = pack && pack.items.find((x) => x.id === en.target.dataset.id);
                if (!it) continue;
                it._vis = en.isIntersecting;
                if (en.isIntersecting) any = true;
            }
            if (any) scheduleStrip();
        }, { root: $('pfStripTrack'), rootMargin: '0px 240px' });
    }
    stripObserver.observe(el);
}

function shotEl(it) {
    const wrap = document.createElement('div');
    wrap.className = 'pf-shot' + (it.state === 'loading' ? ' is-loading' : '');
    wrap.dataset.id = it.id;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'pf-shot-pick';
    b.setAttribute('role', 'option');
    b.setAttribute('aria-selected', 'false');
    b.tabIndex = -1;
    // 0×0 until its card is drawn: a bare canvas is 300×150 and showed as an
    // empty 104-px slot for the moment between «decoded» and «painted».
    const cv0 = document.createElement('canvas');
    cv0.width = 0; cv0.height = 0;
    b.appendChild(cv0);
    // Mouse and finger only: the keyboard takes a photo out with Delete on the
    // focused tile, so the listbox keeps one control per option.
    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'pf-shot-del';
    del.tabIndex = -1;
    del.setAttribute('aria-hidden', 'true');
    del.title = T('ui.pack_remove');
    del.textContent = '×';
    const dot = document.createElement('span');
    dot.className = 'pf-shot-dot';
    dot.title = T('ui.pack_touched');
    dot.hidden = true;
    wrap.append(b, del, dot);
    it._el = wrap;
    it._painted = '';
    observeShot(wrap);
    return wrap;
}

function shotReady(it) {
    if (!it._el) return;
    it._el.classList.remove('is-loading');
    // Drawn at once, not on the next debounced pass: the tile turns from a
    // placeholder into its card in the same frame (a few ms per tile).
    paintShot(it);
    it._painted = `${designSig()}#${JSON.stringify(it.slot)}`;
}

function syncStripSel(reveal) {
    if (!pack) return;
    const n = pack.items.length;
    pack.items.forEach((it, i) => {
        if (!it._el) return;
        const on = i === pack.cur;
        it._el.classList.toggle('is-on', on);
        const b = it._el.querySelector('.pf-shot-pick');
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
        b.setAttribute('aria-label', `${T('ui.pack_shot').replace(':i', i + 1).replace(':n', n)}: ${it.name}`);
    });
    updatePackNav();
    const cur = curItem();
    if (reveal && cur && cur._el) revealShot(cur._el);
}

/* Bring the current tile into the ribbon's view — by scrolling the ribbon
   itself, never the page. */
function revealShot(el) {
    const track = $('pfStripTrack');
    const tr = track.getBoundingClientRect(), r = el.getBoundingClientRect();
    if (!tr.width) return;
    if (r.left < tr.left + 6) track.scrollLeft -= (tr.left + 6 - r.left);
    else if (r.right > tr.right - 6) track.scrollLeft += (r.right - tr.right + 6);
}

function scheduleStrip() {
    if (!pack || stripTimer) return;
    stripTimer = setTimeout(() => { stripTimer = null; paintStrip(); }, 140);
}

function designSig() {
    const { _locale, photos, ...rest } = doc;
    return `${JSON.stringify(rest)}|${fontEpoch}|${assets.customFrame ? 1 : 0}|${JSON.stringify(packDefaults)}`;
}

function paintStrip() {
    if (!pack) return;
    const ds = designSig();
    for (const it of pack.items) {
        if (!it._el || !it._vis || it.state !== 'ready') continue;
        const sig = `${ds}#${JSON.stringify(it.slot)}`;
        if (it._painted === sig) continue;
        paintShot(it);
        it._painted = sig;
    }
}

function paintShot(it) {
    const c = it._el.querySelector('canvas');
    const d = docFor(it);
    const a = { photos: [proxyAsset(it), null], customFrame: assets.customFrame };
    const ar = docAspect(d, a);
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const h = Math.round(SHOT_H * dpr);
    const w = Math.max(1, Math.round(h * ar));
    if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
    try {
        render(c.getContext('2d'), w, h, d, a, { exporting: true });
    } catch (_) { /* a tile that failed to draw stays as it was */ }
    const dot = it._el.querySelector('.pf-shot-dot');
    if (dot) dot.hidden = !slotTouched(it.slot, packDefaults);
}

/* The photo on the stage at full resolution — a proxy stands in for the ~100 ms
   a decode takes, and nothing that leaves the page may be made from it. */
async function stageFull() {
    const ph = assets.photos[0];
    if (!ph || !ph.proxy) return ph;
    if (pack && curItem()) {
        const it = curItem();
        const full = await fulls.ensure(it, it.id);
        if (curItem() === it && assets.photos[0] === ph) assets.photos[0] = full;
        return full;
    }
    if (SRC.photos[0]) {
        const full = await decodeFile(SRC.photos[0], {});
        if (assets.photos[0] === ph) assets.photos[0] = full;
        return full;
    }
    return ph;
}

/* ─── tabs (icon rail) ────────────────────────────────────────────────────── */

function bindTabs() {
    document.querySelectorAll('.pf-rail [data-tab]').forEach((b) =>
        b.addEventListener('click', () => openTab(b.dataset.tab)));
}

function openTab(name) {
    document.querySelectorAll('.pf-rail [data-tab]').forEach((x) => {
        const on = x.dataset.tab === name;
        x.classList.toggle('is-on', on);
        x.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    document.querySelectorAll('.pf-pane').forEach((p) => { p.hidden = p.dataset.panel !== name; });
    if (name === 'text') onTextTabOpen();
    if (name === 'stickers') ensureStickers();
}

/* ─── aspect menu ─────────────────────────────────────────────────────────── */

/* Grouped by WHAT THE CARD IS FOR, not by arithmetic. «9:16» means nothing to
   someone making a birthday card; «Сторис» does, and the shape next to it says
   the rest. This is the control the last design lost people on. */
const ASPECT_GROUPS = [
    { k: 'auto', items: ['auto'] },
    { k: 'social', items: ['1:1', '4:5', '9:16', '16:9'] },
    { k: 'classic', items: ['4:3', '3:4', '3:2', '2:3'] },
    { k: 'print', items: ['10x15', '15x10v', '13x18', 'a4', 'card'] },
];

let menuFallback = false;

function buildAspectMenu() {
    const menu = $('pfAspectMenu');
    const btn = $('pfAspectBtn');
    menuFallback = !('popover' in HTMLElement.prototype);

    for (const grp of ASPECT_GROUPS) {
        const h = document.createElement('div');
        h.className = 'pf-menu-grp';
        h.textContent = T(`ui.argrp_${grp.k}`);
        menu.appendChild(h);
        const grid = document.createElement('div');
        grid.className = 'pf-menu-grid';
        for (const key of grp.items) {
            const spec = ASPECTS[key];
            if (!spec) continue;
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'pf-ar';
            b.dataset.aspect = key;
            b.setAttribute('role', 'option');
            b.setAttribute('aria-selected', doc.aspect === key ? 'true' : 'false');
            const shape = document.createElement('span');
            shape.className = 'pf-ar-shape';
            const i = document.createElement('i');
            if (spec.w) {
                const ratio = spec.w / spec.h;
                const w = ratio >= 1 ? 32 : Math.round(32 * ratio);
                const h2 = ratio >= 1 ? Math.round(32 / ratio) : 32;
                i.style.width = `${w}px`; i.style.height = `${h2}px`;
            } else {
                i.style.width = '30px'; i.style.height = '24px'; i.style.borderStyle = 'dashed';
            }
            shape.appendChild(i);
            const name = document.createElement('span');
            name.className = 'pf-ar-name';
            name.textContent = T(`aspect.${key.replace(':', '_')}`);
            b.append(shape, name);
            if (spec.printPx) {
                const sub = document.createElement('span');
                sub.className = 'pf-ar-sub';
                sub.textContent = `${spec.printPx[0]}×${spec.printPx[1]}`;
                b.appendChild(sub);
            }
            grid.appendChild(b);
        }
        menu.appendChild(grid);
    }

    menu.addEventListener('click', (e) => {
        const b = e.target.closest('[data-aspect]');
        if (!b) return;
        pushUndo();
        doc.aspect = b.dataset.aspect;
        settings.save('aspect', doc.aspect);
        markEdit();
        syncAspectPill();
        closeMenu();
        layoutCanvas();
        funnel('aspect', { a: doc.aspect });
    });

    btn.addEventListener('click', () => (isMenuOpen() ? closeMenu() : openMenu()));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && isMenuOpen()) closeMenu(); });
    if (menuFallback) {
        document.addEventListener('click', (e) => {
            if (!isMenuOpen()) return;
            if (menu.contains(e.target) || btn.contains(e.target)) return;
            closeMenu();
        });
    }
    window.addEventListener('resize', () => { if (isMenuOpen()) placeMenu(); });
}

function isMenuOpen() {
    const m = $('pfAspectMenu');
    return menuFallback ? m.classList.contains('pf-menu-open') : m.matches(':popover-open');
}
function openMenu() {
    const m = $('pfAspectMenu');
    if (menuFallback) m.classList.add('pf-menu-open');
    else { try { m.showPopover(); } catch (_) { menuFallback = true; m.classList.add('pf-menu-open'); } }
    placeMenu();
    $('pfAspectBtn').setAttribute('aria-expanded', 'true');
    const sel = m.querySelector('[aria-selected=true]') || m.querySelector('[data-aspect]');
    if (sel) sel.focus({ preventScroll: true });
}
function closeMenu() {
    const m = $('pfAspectMenu');
    if (menuFallback) m.classList.remove('pf-menu-open');
    else { try { m.hidePopover(); } catch (_) {} }
    $('pfAspectBtn').setAttribute('aria-expanded', 'false');
}
/** Popover lives in the top layer, so it is positioned against the button's
 *  viewport rect. CSS anchor positioning would do this declaratively but is
 *  Chromium-only today; one rect read per open costs nothing. */
function placeMenu() {
    const m = $('pfAspectMenu');
    const r = $('pfAspectBtn').getBoundingClientRect();
    const w = m.offsetWidth || 360;
    const left = Math.max(12, Math.min(window.innerWidth - w - 12, RTL ? r.right - w : r.left));
    const below = window.innerHeight - r.bottom;
    m.style.left = `${Math.round(left)}px`;
    if (below < 260 && r.top > below) {
        m.style.top = 'auto';
        m.style.bottom = `${Math.round(window.innerHeight - r.top + 6)}px`;
        m.style.maxHeight = `${Math.round(r.top - 18)}px`;
    } else {
        m.style.bottom = 'auto';
        m.style.top = `${Math.round(r.bottom + 6)}px`;
        m.style.maxHeight = `${Math.round(below - 18)}px`;
    }
}

function syncAspectPill() {
    $('pfAspectVal').textContent = T(`aspect.${String(doc.aspect).replace(':', '_')}`);
    document.querySelectorAll('#pfAspectMenu [data-aspect]').forEach((b) =>
        b.setAttribute('aria-selected', b.dataset.aspect === doc.aspect ? 'true' : 'false'));
}

/* ─── layout + slots ──────────────────────────────────────────────────────── */

function bindLayoutSeg() {
    $('pfLayoutSeg').addEventListener('click', (e) => {
        const b = e.target.closest('[data-layout]');
        if (!b) return;
        pushUndo();
        doc.layout = b.dataset.layout;
        if (doc.layout === 'single') activeSlot = 0;
        syncLayoutSeg();
        syncPhotoPanel();
        markEdit();
        layoutCanvas();
        funnel('layout', { l: doc.layout });
    });
    syncLayoutSeg();
}

function syncLayoutSeg() {
    document.querySelectorAll('#pfLayoutSeg [data-layout]').forEach((b) =>
        b.classList.toggle('is-on', b.dataset.layout === doc.layout));
    // «Сколько фото на одной открытке» and «сколько открыток» are two questions:
    // a pack is a card per photo, so the two-window layouts step aside in it,
    // and «＋ Добавить фото» steps aside on a two-window card (it has its own
    // «Второе фото»).
    $('pfLayoutSeg').hidden = !!pack;
    $('pfAddPhoto2').hidden = !!pack || doc.layout === 'single' || !!assets.photos[1];
    $('pfSlotSeg').hidden = !!pack || doc.layout === 'single';
    $('pfPackAdd').hidden = !pack && doc.layout !== 'single';
    document.querySelectorAll('#pfSlotSeg [data-slot]').forEach((b) =>
        b.classList.toggle('is-on', parseInt(b.dataset.slot, 10) === activeSlot));
}

function bindSlotSeg() {
    $('pfSlotSeg').addEventListener('click', (e) => {
        const b = e.target.closest('[data-slot]');
        if (!b) return;
        activeSlot = parseInt(b.dataset.slot, 10) || 0;
        syncLayoutSeg();
        syncPhotoPanel();
        syncCtxBar();
    });
}

function bindSecondPhoto() {
    const input = $('pfPhoto2Input');
    $('pfAddPhoto2').addEventListener('click', () => input.click());
    input.addEventListener('change', async () => {
        if (!input.files || !input.files[0]) return;
        try {
            const src = await ownBytes(input.files[0]);
            const dec = await decodeFile(src, {});
            if (assets.photos[1]) release(assets.photos[1]);
            assets.photos[1] = dec;
            SRC.photos[1] = src;
            doc.photos[1] = newSlot();
            activeSlot = 1;
            markEdit();
            layoutCanvas();
            syncLayoutSeg();
            syncPhotoPanel();
            funnel('second_photo', {});
        } catch (_) {
            toast(T('err.decode'), 'error');
        }
        input.value = '';
    });
}

/* ─── frame gallery (templates merged in) ─────────────────────────────────── */

let currentCat = 'tpl';
let thumbAsset = null;

function sampleAsset() {
    if (thumbAsset) return thumbAsset;
    // A tiny synthetic "photo" for the thumbnails: sky, sun, hills. Drawn once.
    const c = document.createElement('canvas');
    c.width = 240; c.height = 180;
    const x = c.getContext('2d');
    const sky = x.createLinearGradient(0, 0, 0, 180);
    sky.addColorStop(0, '#7cb8e8'); sky.addColorStop(0.65, '#cfe6f4'); sky.addColorStop(1, '#e8d9a8');
    x.fillStyle = sky; x.fillRect(0, 0, 240, 180);
    x.fillStyle = '#f6d76a'; x.beginPath(); x.arc(178, 52, 22, 0, Math.PI * 2); x.fill();
    x.fillStyle = '#7ca35d'; x.beginPath(); x.moveTo(0, 180); x.quadraticCurveTo(70, 96, 150, 168); x.lineTo(0, 180); x.fill();
    x.fillStyle = '#5d8a4a'; x.beginPath(); x.moveTo(240, 180); x.quadraticCurveTo(160, 108, 60, 176); x.lineTo(240, 180); x.fill();
    thumbAsset = { photos: [{ drawable: c, w: 240, h: 180 }, { drawable: c, w: 240, h: 180 }], customFrame: null };
    return thumbAsset;
}

function buildCats() {
    const wrap = $('pfCatChips');
    // «Готовые» first: a template is a frame that arrives already dressed, and
    // it is the fastest route to a finished card. It used to be a separate tab,
    // which split one question — «во что одеть фото» — across two places.
    const rows = [{ id: 'tpl' }, { id: 'my' }, ...catsForLocale(LOCALE)];
    for (const c of rows) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pf-chip' + (c.id === currentCat ? ' is-on' : '');
        b.dataset.cat = c.id;
        b.textContent = c.id === 'tpl' ? `🪄 ${T('ui.cat_templates')}`
            : c.id === 'my' ? `⭐ ${T('ui.my_templates')}`
                : T(`cat.${c.id}`);
        if (c.id === 'my') b.hidden = !myTpls().length;
        wrap.appendChild(b);
    }
    wrap.addEventListener('click', (e) => {
        const b = e.target.closest('[data-cat]');
        if (!b) return;
        currentCat = b.dataset.cat;
        wrap.querySelectorAll('.pf-chip').forEach((x) => x.classList.toggle('is-on', x === b));
        buildFrameGallery();
        funnel('cat', { c: currentCat });
    });
}

let thumbObserver = null;

function buildFrameGallery() {
    const grid = $('pfFrameGrid');
    grid.textContent = '';
    if (thumbObserver) thumbObserver.disconnect();
    // ~120 thumbnails would cost a visible stall if painted eagerly; each cell
    // renders on first approach to the viewport instead. The gallery itself is
    // the scroller (the pane's chips and settings stay put around it).
    const scrollRoot = grid;
    thumbObserver = new IntersectionObserver((entries) => {
        for (const en of entries) {
            if (!en.isIntersecting) continue;
            paintThumb(en.target);
            thumbObserver.unobserve(en.target);
        }
    }, { root: scrollRoot, rootMargin: '300px' });

    // Frame setup stays put on every tab: it configures the frame currently ON
    // the canvas, not the gallery being browsed. Hiding it while «Готовые» was
    // open meant picking a ready card and then having to leave the tab to make
    // its border thinner.
    if (currentCat === 'tpl') { buildTemplateCards(grid); return; }
    if (currentCat === 'my') { buildMyCards(grid); return; }

    const addThumb = (f) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pf-frame-thumb' + (doc.frame === f.id ? ' is-on' : '');
        b.dataset.frame = f.id;
        b.setAttribute('aria-label', T(`cat.${f.cat}`));
        const c = document.createElement('canvas');
        c.width = 132; c.height = 104;
        b.appendChild(c);
        grid.appendChild(b);
        thumbObserver.observe(b);
    };
    if (currentCat === 'all') {
        // «Все» reads as a catalogue, not a wall: each category gets its label
        // row, so 120+ thumbs stay scannable.
        const all = framesForLocale(LOCALE);
        for (const cat of catsForLocale(LOCALE)) {
            if (cat.id === 'all') continue;
            const group = all.filter((f) => f.cat === cat.id);
            if (!group.length) continue;
            const h = document.createElement('div');
            h.className = 'pf-grid-head';
            h.append(T(`cat.${cat.id}`), Object.assign(document.createElement('small'), { textContent: group.length }));
            grid.appendChild(h);
            group.forEach(addThumb);
        }
    } else {
        framesForLocale(LOCALE).filter((f) => f.cat === currentCat).forEach(addThumb);
    }
    grid.onclick = (e) => {
        const b = e.target.closest('[data-frame]');
        if (!b) return;
        pushUndo();
        doc.frame = b.dataset.frame;
        hoverFrame = null;
        grid.querySelectorAll('.pf-frame-thumb').forEach((x) => x.classList.toggle('is-on', x === b));
        markEdit();
        syncTintRow();
        requestRender();
        funnel('frame_pick', { f: doc.frame });
    };
    // Живая примерка мышью (только точные указатели: на таче hover — фантом).
    if (window.matchMedia && matchMedia('(hover: hover) and (pointer: fine)').matches) {
        grid.onpointerover = (e) => {
            const b = e.target.closest('[data-frame]');
            if (!b || !assets.photos[0]) return;
            hoverFrame = b.dataset.frame;
            requestRender();
        };
        grid.onpointerleave = () => {
            if (!hoverFrame) return;
            hoverFrame = null;
            requestRender();
        };
    }
}

/**
 * Template cards are RENDERED, not illustrated.
 *
 * They used to be an emoji + a name + the greeting as plain text, which asked
 * the user to imagine the result. A card that shows the actual frame with the
 * actual phrase in the actual font answers the only question being asked.
 */
function buildTemplateCards(grid) {
    warmTextFonts();
    for (const tpl of templatesForLocale(LOCALE)) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pf-frame-thumb';
        b.dataset.tpl = tpl.id;
        const c = document.createElement('canvas');
        c.width = 132; c.height = 165;
        const cap = document.createElement('figcaption');
        cap.textContent = T(`tpl.${tpl.id}.name`);
        b.append(c, cap);
        b.setAttribute('aria-label', T(`tpl.${tpl.id}.name`));
        grid.appendChild(b);
        thumbObserver.observe(b);
    }
    grid.onpointerover = null;
    grid.onpointerleave = null;
    grid.onclick = (e) => {
        const b = e.target.closest('[data-tpl]');
        if (b) applyTemplate(b.dataset.tpl);
    };
}

function buildMyCards(grid) {
    const list = myTpls();
    if (!list.length) {
        const p = document.createElement('p');
        p.className = 'pf-hint';
        p.style.gridColumn = '1/-1';
        p.textContent = T('ui.my_empty');
        grid.appendChild(p);
        return;
    }
    for (const item of list) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pf-frame-thumb';
        b.dataset.my = item.id;
        const c = document.createElement('canvas');
        c.width = 132; c.height = 165;
        const cap = document.createElement('figcaption');
        cap.textContent = item.name;
        b.append(c, cap);
        grid.appendChild(b);
        thumbObserver.observe(b);
    }
    grid.onpointerover = null;
    grid.onpointerleave = null;
    grid.onclick = (e) => {
        const b = e.target.closest('[data-my]');
        if (!b) return;
        const item = myTpls().find((x) => x.id === b.dataset.my);
        if (!item) return;
        pushUndo();
        doc = Object.assign({}, newDoc(), item.doc, { _locale: LOCALE });
        // In a pack a saved look dresses the pack: each photo keeps its own crop,
        // and a two-window look stays a card per photo.
        if (pack && curItem()) { doc.photos = [curItem().slot, newSlot()]; doc.layout = 'single'; }
        selection = null;
        markEdit();
        syncPanelsFromDoc();
        syncTintRow();
        for (const t of doc.texts) loadFont(t.font, LOCALE).then(fontLoaded);
        layoutCanvas();
        funnel('my_tpl_apply', {});
    };
}

function paintThumb(btn) {
    const c = btn.querySelector('canvas');
    if (!c) return;
    const x = c.getContext('2d');
    let mini;
    if (btn.dataset.tpl) mini = templateDoc(btn.dataset.tpl);
    else if (btn.dataset.my) {
        const item = myTpls().find((v) => v.id === btn.dataset.my);
        mini = item ? Object.assign({}, newDoc(), item.doc, { _locale: LOCALE }) : null;
    } else {
        mini = { ...newDoc(), frame: btn.dataset.frame, layout: 'single', _locale: LOCALE };
    }
    if (!mini) return;
    try {
        render(x, c.width, c.height, mini, sampleAsset(), { skipTexts: !btn.dataset.tpl && !btn.dataset.my });
    } catch (_) {
        x.fillStyle = '#333'; x.fillRect(0, 0, c.width, c.height);
    }
}

/* ─── frame options (width · corner · tint · own PNG) ─────────────────────── */

const TINTS = ['#f6f4ef', '#ffffff', '#141414', '#f3d9df', '#dfe8f3', '#e4ecdd', '#f3ead8', '#7e1024', '#1d2a44', '#d8b756'];

function buildTintRow() {
    const wrap = $('pfSwatches');
    for (const c of TINTS) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pf-swatch';
        b.dataset.tint = c;
        b.style.background = c;
        b.setAttribute('aria-label', c);
        wrap.appendChild(b);
    }
    wrap.addEventListener('click', (e) => {
        const b = e.target.closest('[data-tint]');
        if (b === null) return;
        pushUndo();
        doc.frameTint = b.dataset.tint || null;
        markEdit();
        syncTintRow();
        requestRender();
        funnel('frame_tint', { t: doc.frameTint || 'off' });
    });
    $('pfTintCustom').addEventListener('input', () => {
        pushUndo();
        doc.frameTint = $('pfTintCustom').value;
        markEdit();
        syncTintRow();
        requestRender();
    });
    bindEyedropper('pfTintPick', (hex) => {
        pushUndo();
        doc.frameTint = hex;
        $('pfTintCustom').value = hex;
        markEdit(); syncTintRow(); requestRender();
    });
}

function bindFrameOpts() {
    // The two knobs Fotor made table stakes and we shipped without: how wide
    // the frame is and how round the window is. Both are multipliers on the
    // catalogue frame, so «золотой багет» stays gold at any width.
    bindRange('pfFrameW', 'pfFrameWVal', (v) => { doc.frameW = v / 100; }, (v) => `${Math.round(v)}%`, layoutCanvas);
    bindRange('pfWinR', 'pfWinRVal', (v) => { doc.winR = v < 0 ? null : v / 100; },
        (v) => (v < 0 ? T('ui.corner_frame') : `${Math.round(v)}%`), layoutCanvas);
    bindRange('pfCapBand', 'pfCapBandVal', (v) => { doc.capBand = v / 100; },
        (v) => (v > 0 ? `${Math.round(v)}%` : T('ui.cap_none')), layoutCanvas);
}

function syncTintRow() {
    const f = frameById(doc.frame);
    $('pfTintRow').hidden = !f.tintable;
    document.querySelectorAll('#pfSwatches [data-tint]').forEach((b) =>
        b.classList.toggle('is-on', (b.dataset.tint || null) === doc.frameTint));
}

/* Дверь «Нарисовать рамку ИИ вокруг вашего фото» (есть только там, где генератор
   открыт). Снимок едет переносом (tool-handoff.js) вместе с подсказкой: режим
   правки, «Рамка: » и «только вокруг фото» — генератор вернёт его без изменений.
   Едет уже раскодированный кадр (HEIC и поворот по EXIF сделаны здесь). Перенос
   провязывается на DOMContentLoaded: tool-handoff.js — отложенный скрипт layout'а. */
function bindAiFrameDoor() {
    const door = $('pfAiFrame');
    if (!door) return;
    door.addEventListener('click', () => funnel('ai_frame_door', { photo: assets.photos[0] ? 1 : 0 }));
    const wire = () => {
        const th = window.__toolHandoff;
        if (!th || typeof th.carry !== 'function') return;
        th.carry(door, () => {
            const ph = assets.photos[0];
            if (!ph || !ph.drawable) return null;
            const base = (SRC.photos[0] && SRC.photos[0].name ? String(SRC.photos[0].name).replace(/\.[a-z0-9]{2,5}$/i, '') : 'photo').slice(0, 60);
            return {
                size: Math.round(ph.w * ph.h * 0.25), type: 'image/jpeg', filename: base + '.jpg',
                hints: { mode: 'edit', prompt: T('ui.ai_frame_prompt'), around: 10 },
                make: async () => {
                    // A photo of a pack may still be its small stand-in on the stage.
                    const full = (await stageFull().catch(() => null)) || ph;
                    const k = Math.min(1, 4096 / Math.max(full.w, full.h));
                    const c = document.createElement('canvas');
                    c.width = Math.max(1, Math.round(full.w * k)); c.height = Math.max(1, Math.round(full.h * k));
                    c.getContext('2d').drawImage(full.drawable, 0, 0, c.width, c.height);
                    return window.__canvasBlob(c, 'image/jpeg', 0.93);
                },
            };
        });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire, { once: true });
    else wire();
}

function bindCustomFrame() {
    const input = $('pfCustomFrameInput');
    $('pfCustomFrameBtn').addEventListener('click', () => {
        if (doc.customFrame) {
            pushUndo();
            doc.customFrame = false;
            if (assets.customFrame) { release(assets.customFrame); assets.customFrame = null; }
            SRC.frame = null;
            $('pfCustomFrameBtn').classList.remove('is-on');
            $('pfCustomFrameBtn').textContent = T('ui.custom_frame');
            markEdit();
            requestRender();
            return;
        }
        input.click();
    });
    input.addEventListener('change', async () => {
        if (!input.files || !input.files[0]) return;
        try {
            const src = await ownBytes(input.files[0]);
            const dec = await decodeFile(src, {});
            // A twibbon overlay without transparency would simply hide the
            // photo — worth a warning, not a refusal (the file may be wrong).
            if (!dec.hasAlpha) toast(T('warn.frame_no_alpha'), 'warning');
            pushUndo();
            if (assets.customFrame) release(assets.customFrame);
            assets.customFrame = dec;
            SRC.frame = src;
            doc.customFrame = true;
            $('pfCustomFrameBtn').classList.add('is-on');
            $('pfCustomFrameBtn').textContent = T('ui.custom_frame_on');
            markEdit();
            requestRender();
            funnel('custom_frame', { alpha: dec.hasAlpha ? 1 : 0 });
        } catch (_) {
            toast(T('err.decode'), 'error');
        }
        input.value = '';
    });
}

/* ─── templates ───────────────────────────────────────────────────────────── */

function templateDoc(id) {
    const tpl = templatesForLocale(LOCALE).find((t) => t.id === id);
    if (!tpl) return null;
    const d = newDoc();
    d._locale = LOCALE;
    d.frame = tpl.frame;
    // A greeting authored at y≈0.87 belongs on paper, not on the photo. Open a
    // caption band whenever the template writes down there; the colours in
    // templates.js were picked for a mat, and without the band they were being
    // painted onto whatever the photograph happened to have at the bottom.
    d.capBand = tpl.texts.some((t) => (t.y || 0) >= 0.8) ? 0.22 : 0;
    d.texts = tpl.texts.map((spec) => {
        // `raw` is литерал (юбилейная цифра «50») — числа не переводятся.
        const value = spec.raw != null ? spec.raw : T(`tpl.${id}.${spec.k}`);
        return newText(value, {
            font: spec.font,
            size: spec.size,
            y: spec.y,
            x: spec.x != null ? spec.x : 0.5,
            color: spec.color || spec.dark || '#ffffff',
            shadow: spec.shadow != null ? spec.shadow : !spec.dark,
            stroke: spec.strokeW || 0,
            strokeColor: spec.strokeColor || '#000000',
            rtl: RTL,
        });
    });
    return d;
}

function applyTemplate(id) {
    const d = templateDoc(id);
    if (!d) return;
    pushUndo();
    // A template dresses the photo; it must not throw away the crop the user
    // already made or the canvas shape they chose for a specific destination.
    doc.frame = d.frame;
    doc.frameTint = null;
    doc.frameW = 1;
    doc.winR = null;
    doc.capBand = d.capBand;
    doc.texts = d.texts;
    for (const t of doc.texts) loadFont(t.font, LOCALE).then(fontLoaded);
    selection = doc.texts.length ? { kind: 'text', id: doc.texts[0].id } : null;
    markEdit();
    syncPanelsFromDoc();
    syncTintRow();
    layoutCanvas();
    announce(T('msg.tpl_applied'));
    funnel('tpl_apply', { tpl: id });
    // The phrase is a starting point — put the user one tap from editing it.
    openTab('text');
}

/* ─── my templates (localStorage) ─────────────────────────────────────────── */

const MYTPL_KEY = 'pf:mytpl';

function myTpls() {
    try { return JSON.parse(localStorage.getItem(MYTPL_KEY)) || []; } catch (_) { return []; }
}
function persistMyTpls(list) {
    try { localStorage.setItem(MYTPL_KEY, JSON.stringify(list.slice(0, 24))); } catch (_) {}
}

function saveMyTpl() {
    const { _locale, ...snap } = doc;
    const list = myTpls();
    list.unshift({
        id: 'm' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5),
        name: `${T('ui.my_tpl_prefix')} ${list.length + 1}`,
        doc: snap,
    });
    persistMyTpls(list);
    const chip = document.querySelector('#pfCatChips [data-cat=my]');
    if (chip) chip.hidden = false;
    if (currentCat === 'my') buildFrameGallery();
    toast(T('msg.tpl_saved'), 'success');
    funnel('my_tpl_save', { n: list.length });
}

/* ─── «Мои работы»: the card survives a crash, a closed tab and F5 ─────────────
   Audit of 30.09.2026: the card lived in memory only — the photos, the frame,
   the greeting and the crop were gone with a crash, a closed tab or F5, and the
   leave-page question above was the whole defence. The shared controller
   (public/js/tool-draft-editor.js over tool-draft.js, in the `work` storage of
   work-storage.js) keeps it now; this part says what the card IS, how it comes
   back and what a new one looks like.
   ⚠️ «Мои шаблоны» above are something else: presets of a look (no photos), kept
   in localStorage to dress the next photo. They are not the work and stay apart. */
let drafts = null;
let freshening = false;              // draftFresh() is putting the zone back itself
let previewCache = { url: '', at: 0 };

function draftTouch(counted) { if (drafts) drafts.touch(counted); }

/* Leaving loses nothing when the card is kept: saved here at least once, the last
   save did not fail — a pending change goes out with the page (pagehide). */
function workKept() {
    if (!drafts || !drafts.live() || !drafts.store() || !drafts.id()) return false;
    const s = $('pfDraftsSave');
    return !!s && !s.hidden && !s.classList.contains('bad');
}

/* What a person calls the card: its greeting (first line of the first caption
   that is not the placeholder), else the photo's name. */
function draftLabel() {
    const ph = T('ui.text_placeholder');
    const t = doc.texts.find((x) => { const v = String(x.value || '').trim(); return v && v !== ph; });
    const line = t ? String(t.value).trim().split(/\r?\n/)[0].trim() : '';
    if (line) return line.slice(0, 80);
    const f = SRC.photos[0];
    return f && f.name ? String(f.name).replace(/\.[a-z0-9]{2,5}$/i, '').slice(0, 80) : '';
}

/* A small picture of the card for the list — the same render() as the export,
   so no selection ring and no hover try-on. */
function draftPreview() {
    const now = Date.now();
    if (previewCache.url && now - previewCache.at < 1500) return previewCache.url;
    try {
        const ar = docAspect(doc, assets);
        const w = ar >= 1 ? 160 : Math.max(1, Math.round(160 * ar));
        const h = ar >= 1 ? Math.max(1, Math.round(160 / ar)) : 160;
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const x = c.getContext('2d');
        render(x, w, h, doc, assets, { exporting: true });
        x.globalCompositeOperation = 'destination-over';     // a see-through frame on paper, not on black
        x.fillStyle = '#ffffff';
        x.fillRect(0, 0, w, h);
        previewCache = { url: c.toDataURL('image/jpeg', 0.72), at: now };
    } catch (_) { /* a row without a picture still opens the card */ }
    return previewCache.url;
}

function draftPayload() {
    if (pack) return packPayload();
    // every picture on the card travels with its bytes (they are set together)
    if ((assets.photos[0] && !SRC.photos[0]) || (assets.photos[1] && !SRC.photos[1])) return null;
    const files = [], at = {};
    const add = (key, f) => { at[key] = files.length; files.push(f); };
    if (SRC.photos[0]) add('p0', SRC.photos[0]);
    if (SRC.photos[1]) add('p1', SRC.photos[1]);
    const own = !!(doc.customFrame && assets.customFrame && SRC.frame);
    if (own) add('frame', SRC.frame);
    const state = { doc: JSON.parse(designJson()), files: at };
    state.doc.customFrame = own;
    return {
        state, files,
        label: draftLabel(),
        preview: draftPreview(),
        meta: { photos: (SRC.photos[0] ? 1 : 0) + (SRC.photos[1] ? 1 : 0), texts: doc.texts.length, frame: doc.frame },
    };
}

/* A pack as a work: the design, and every photo with its bytes and its own
   placement, in ribbon order. Nothing while photos are still decoding — a save
   then would keep a pack that is missing them. */
function packPayload() {
    if (packLoading()) return null;
    const files = [], at = {};
    const items = pack.items.map((it) => {
        files.push(it.file);
        return { f: files.length - 1, name: it.name, slot: it.slot };
    });
    const own = !!(doc.customFrame && assets.customFrame && SRC.frame);
    if (own) { at.frame = files.length; files.push(SRC.frame); }
    const state = { doc: JSON.parse(designJson()), files: at, pack: { cur: pack.cur, items } };
    state.doc.customFrame = own;
    const first = pack.items[0];
    const named = draftLabel();
    return {
        state, files,
        // «IMG_0412.jpg +11» — the uploader's own way of naming a pick of many.
        label: named && !(first && named === String(first.name).replace(/\.[a-z0-9]{2,5}$/i, '').slice(0, 80))
            ? named : `${first ? String(first.name).slice(0, 60) : ''} +${pack.items.length - 1}`,
        preview: draftPreview(),
        meta: { photos: pack.items.length, texts: doc.texts.length, frame: doc.frame },
    };
}

/* A saved pack back on the page: the design at once, the photos one by one into
   the ribbon (the one that was on the stage first). → the names that did not
   come back. */
async function applyPackDraft(d, saved) {
    const st = d.state || {};
    const at = st.files || {};
    const files = d.files || [];
    const names = (d.fileMeta || []).map((m) => (m && m.name) || '');
    const missing = [];
    let fr = null;
    if (saved.customFrame && at.frame != null) {
        const f = files[at.frame];
        if (!f) missing.push(names[at.frame] || 'frame');
        else { try { fr = { src: f, dec: await decodeFile(f, {}) }; } catch (_) { missing.push(f.name || 'frame'); } }
    }
    for (const p of assets.photos) if (p) release(p);
    if (assets.customFrame) release(assets.customFrame);
    dropPack();
    assets = { photos: [null, null], customFrame: fr ? fr.dec : null };
    SRC = { photos: [null, null], frame: fr ? fr.src : null };
    doc = savedDoc(saved);
    doc.layout = 'single';
    doc.customFrame = !!assets.customFrame;
    selection = null;
    activeSlot = 0;
    hoverFrame = null;
    pendingTpl = null;
    undoStack.length = 0; redoStack.length = 0;
    const m = d.meta || {};
    dirty = (m.editedAt || 0) > (m.exportedAt || 0);
    previewCache = { url: '', at: 0 };
    for (const t of doc.texts) loadFont(t.font, LOCALE).then(fontLoaded);

    const gen = ++packGen;
    const items = [];
    for (const rec of st.pack.items) {
        const f = rec ? files[rec.f] : null;
        if (!f) { missing.push((rec && (names[rec.f] || rec.name)) || ''); continue; }
        const s = (rec && rec.slot) || {};
        items.push(pendingItem(f, Object.assign(newSlot(), s, {
            pan: Object.assign({ x: 0, y: 0 }, s.pan),
            adjust: Object.assign(newSlot().adjust, s.adjust),
        })));
    }
    const want = Math.max(0, Math.min(items.length - 1, st.pack.cur | 0));
    pack = { items, cur: want };
    buildStrip();

    const app = $('pfApp');
    app.hidden = false;
    app.classList.add('is-editing');
    syncPanelsFromDoc();
    syncFrameUi();
    openTab('frames');
    const order = items.length ? [items[want], ...items.filter((_, i) => i !== want)] : [];
    const failed = await ingestItems(order, gen);
    missing.push(...failed);
    if (gen === packGen && pack) settlePack({ restoring: true });
    if (!pack && !assets.photos[0]) {
        try { const z = $('pfDrop'); if (z && z.__uploaderRestore) z.__uploaderRestore(true); } catch (_) {}
    }
    return missing;
}

/* A saved doc onto a new one: a field the saved card predates gets its default. */
function savedDoc(saved) {
    const d = Object.assign(newDoc(), saved, { _locale: LOCALE });
    if (!ASPECTS[d.aspect]) d.aspect = 'auto';
    if (!['single', 'double-h', 'double-v'].includes(d.layout)) d.layout = 'single';
    const slots = Array.isArray(saved.photos) ? saved.photos : [];
    d.photos = [0, 1].map((i) => {
        const s = slots[i] || {};
        return Object.assign(newSlot(), s, {
            pan: Object.assign({ x: 0, y: 0 }, s.pan),
            adjust: Object.assign(newSlot().adjust, s.adjust),
        });
    });
    d.texts = (Array.isArray(saved.texts) ? saved.texts : []).map((t) => newText(t.value, t));
    d.stickers = (Array.isArray(saved.stickers) ? saved.stickers : []).map((s) => newSticker(s.char, s));
    return d;
}

/* Put a saved card back: its pictures through the same decode as a pick, its doc
   onto a new one, the editor opened the way a pick opens it. → the names of the
   files that did not come back (the card keeps its frame and words without them). */
async function applyDraft(d) {
    const st = d.state || {};
    const saved = st.doc && typeof st.doc === 'object' ? st.doc : {};
    if (st.pack && Array.isArray(st.pack.items) && st.pack.items.length) return applyPackDraft(d, saved);
    const at = st.files || {};
    const files = d.files || [];
    const names = (d.fileMeta || []).map((m) => (m && m.name) || '');
    const missing = [];
    const take = async (key) => {
        if (at[key] == null) return null;
        const f = files[at[key]];
        if (!f) { missing.push(names[at[key]] || key); return null; }
        try { return { src: f, dec: await decodeFile(f, {}) }; } catch (_) { missing.push(f.name || key); return null; }
    };
    const p0 = await take('p0');
    const p1 = await take('p1');
    const fr = saved.customFrame ? await take('frame') : null;

    for (const p of assets.photos) if (p) release(p);
    if (assets.customFrame) release(assets.customFrame);
    dropPack();                          // a pack on the page gives way to the single card chosen
    assets = { photos: [p0 ? p0.dec : null, p1 ? p1.dec : null], customFrame: fr ? fr.dec : null };
    // the very File objects the store handed back: its next save sees the same bytes and leaves them be
    SRC = { photos: [p0 ? p0.src : null, p1 ? p1.src : null], frame: fr ? fr.src : null };
    doc = savedDoc(saved);
    doc.customFrame = !!assets.customFrame;
    selection = null;
    activeSlot = 0;
    hoverFrame = null;
    pendingTpl = null;                   // a card was chosen — a ?tpl= link must not redress it
    undoStack.length = 0; redoStack.length = 0;
    const m = d.meta || {};
    dirty = (m.editedAt || 0) > (m.exportedAt || 0);
    previewCache = { url: '', at: 0 };
    for (const t of doc.texts) loadFont(t.font, LOCALE).then(fontLoaded);

    const app = $('pfApp');
    app.hidden = false;
    app.classList.add('is-editing');
    layoutCanvas();
    syncPanelsFromDoc();
    syncFrameUi();
    updateExpEstimate();
    openTab('frames');
    // The zone says which photo is on the card, as after a pick: «Другой файл»
    // swaps it, × starts over. A card whose photo did not come back waits for one.
    const zone = $('pfDrop');
    const shown = SRC.photos.filter(Boolean);
    try {
        if (zone && shown.length && zone.__uploaderCollapse) zone.__uploaderCollapse(shown);
        else if (zone && zone.__uploaderRestore) zone.__uploaderRestore(true);
    } catch (_) {}
    return missing;
}

/* A new card: the page as a new visit finds it, the zone waiting for a photo. */
function draftFresh() {
    resetAll();
    previewCache = { url: '', at: 0 };
    freshening = true;
    try { if (window.ToolUploader) window.ToolUploader.clear('pfDrop'); } catch (_) {}
    freshening = false;
}

/* The frame controls syncPanelsFromDoc leaves alone: the tint row, the picked
   thumb in the gallery and the own-frame chip. A card that arrives whole
   (restore) or leaves whole (start over) must not keep the previous card's. */
function syncFrameUi() {
    syncTintRow();
    document.querySelectorAll('#pfFrameGrid [data-frame]').forEach((x) => x.classList.toggle('is-on', x.dataset.frame === doc.frame));
    const b = $('pfCustomFrameBtn');
    if (!b) return;
    const on = !!(doc.customFrame && assets.customFrame);
    b.classList.toggle('is-on', on);
    b.textContent = T(on ? 'ui.custom_frame_on' : 'ui.custom_frame');
}

function draftsBoot() {
    if (!window.__toolDraft || !window.__toolDraft.editor || !$('pfDrafts')) {
        // the store did not load: no list to offer (the leave-page question keeps guarding)
        const b = $('pfDraftsListBtn');
        if (b) b.hidden = true;
        return;
    }
    drafts = window.__toolDraft.editor({
        slug: SLUG, root: 'pfDrafts', icon: '🖼️',
        payload: draftPayload, apply: applyDraft, fresh: draftFresh,
        started: () => picking > 0 || !!assets.photos[0] || !!pendingTpl || !!pack,
    });
    drafts.boot();
}

/* ─── text panel ──────────────────────────────────────────────────────────── */

let fontsWarmed = false;

function warmTextFonts() {
    if (fontsWarmed) return;
    fontsWarmed = true;
    warmAll(LOCALE, (id) => {
        const el = document.querySelector(`#pfFontGrid [data-font="${id}"] .pf-font-sample`);
        if (el) el.style.fontFamily = cssFamily(id);
        fontLoaded();
        if (currentCat === 'tpl') document.querySelectorAll('#pfFrameGrid [data-tpl]').forEach(paintThumb);
    });
}

function onTextTabOpen() {
    warmTextFonts();
    if (!doc.texts.length) addTextLayer(T('ui.text_placeholder'));
    else if (!selection || selection.kind !== 'text') {
        selection = { kind: 'text', id: doc.texts[doc.texts.length - 1].id };
        syncTextPanel();
        requestRender();
    }
}

function addTextLayer(value) {
    pushUndo();
    const t = newText(value, { rtl: RTL, y: doc.texts.length ? 0.5 : 0.85 });
    doc.texts.push(t);
    selection = { kind: 'text', id: t.id };
    markEdit();
    syncTextPanel();
    syncCtxBar();
    requestRender();
    funnel('text_add', { n: doc.texts.length });
}

function selectedText() {
    if (!selection || selection.kind !== 'text') return null;
    return doc.texts.find((t) => t.id === selection.id) || null;
}

/**
 * One tap to a caption that READS on a photo.
 *
 * Six sliders is not a choice, it is work — and the work has one correct
 * answer most of the time: light text needs a dark edge, dark text needs a
 * light one. Each preset sets font, colour, outline, shadow and plate together.
 */
const TEXT_STYLES = [
    { id: 'clean', font: 'caveat', color: '#ffffff', stroke: 0, shadow: true, bg: null },
    { id: 'outline', font: 'montserrat', color: '#ffffff', stroke: 0.14, strokeColor: '#101010', shadow: false, bg: null },
    { id: 'gold', font: 'yeseva', color: '#f0cf7a', stroke: 0.07, strokeColor: '#6b4c10', shadow: true, bg: null },
    { id: 'ink', font: 'playfair', color: '#20242c', stroke: 0, shadow: false, bg: null },
    { id: 'plate', font: 'montserrat', color: '#ffffff', stroke: 0, shadow: false, bg: '#000000' },
    { id: 'neon', font: 'pacifico', color: '#7ef0ff', stroke: 0.1, strokeColor: '#0b2c46', shadow: true, bg: null },
];

/** Relative luminance of a #rrggbb colour, 0..1. */
function luma(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
    if (!m) return 1;
    const n = parseInt(m[1], 16);
    return (0.2126 * (n >> 16 & 255) + 0.7152 * (n >> 8 & 255) + 0.0722 * (n & 255)) / 255;
}

function buildTextStyles() {
    const wrap = $('pfTextStyles');
    for (const s of TEXT_STYLES) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pf-tstyle';
        b.dataset.tstyle = s.id;
        b.textContent = T(`tstyle.${s.id}`);
        b.style.color = s.color;
        // The chip previews the preset by BEING it, so its plate has to be
        // chosen against the preset's own colour — a fixed dark plate made the
        // «Тёмный» preset invisible on the dark theme, i.e. the one control
        // that shows you what you get showed nothing.
        b.style.background = luma(s.color) > 0.55 ? '#2a2f3a' : '#e8eaef';
        if (s.stroke) b.style.textShadow = `0 0 2px ${s.strokeColor}, 0 0 3px ${s.strokeColor}`;
        wrap.appendChild(b);
    }
    wrap.addEventListener('click', (e) => {
        const b = e.target.closest('[data-tstyle]');
        const t = selectedText();
        if (!b || !t) return;
        const s = TEXT_STYLES.find((x) => x.id === b.dataset.tstyle);
        pushUndo();
        t.font = s.font; t.color = s.color; t.stroke = s.stroke;
        t.strokeColor = s.strokeColor || '#000000';
        t.shadow = s.shadow; t.bg = s.bg;
        loadFont(t.font, LOCALE).then(fontLoaded);
        markEdit();
        syncTextPanel();
        requestRender();
        funnel('text_style', { s: s.id });
    });
}

function bindTextPanel() {
    $('pfAddText').addEventListener('click', () => {
        addTextLayer(T('ui.text_placeholder'));
        $('pfTextInput').focus();
        $('pfTextInput').select();
    });
    $('pfTextDel').addEventListener('click', () => removeSelected());
    $('pfTextDup').addEventListener('click', () => duplicateSelected());
    $('pfTextInput').addEventListener('input', () => {
        const t = selectedText();
        if (!t) return;
        pushUndo();
        t.value = $('pfTextInput').value;
        markEdit();
        syncTextChips();
        requestRender();
    });
    $('pfFontGrid').addEventListener('click', (e) => {
        const b = e.target.closest('[data-font]');
        const t = selectedText();
        if (!b || !t) return;
        pushUndo();
        t.font = b.dataset.font;
        loadFont(t.font, LOCALE).then(fontLoaded);
        markEdit();
        syncFontGrid();
        requestRender();
        funnel('font', { f: t.font });
    });
    bindRange('pfTextSize', 'pfTextSizeVal', (v) => { const t = selectedText(); if (t) t.size = v / 1000; }, (v) => `${Math.round(v / 10)}`);
    bindRange('pfStrokeW', 'pfStrokeVal', (v) => { const t = selectedText(); if (t) t.stroke = v / 100; }, (v) => `${Math.round(v)}`);
    bindRange('pfTextRot', 'pfTextRotVal', (v) => { const t = selectedText(); if (t) t.rot = v; }, (v) => `${Math.round(v)}°`);
    bindRange('pfTextCurve', 'pfTextCurveVal', (v) => { const t = selectedText(); if (t) t.curve = v; }, (v) => `${Math.round(v)}`);
    $('pfTextColor').addEventListener('input', () => { const t = selectedText(); if (t) { pushUndo(); t.color = $('pfTextColor').value; markEdit(); requestRender(); } });
    bindEyedropper('pfTextPick', (hex) => {
        const t = selectedText();
        if (!t) return;
        pushUndo(); t.color = hex; $('pfTextColor').value = hex; markEdit(); requestRender();
    });
    $('pfStrokeColor').addEventListener('input', () => { const t = selectedText(); if (t) { pushUndo(); t.strokeColor = $('pfStrokeColor').value; markEdit(); requestRender(); } });
    $('pfTextShadow').addEventListener('change', () => { const t = selectedText(); if (t) { pushUndo(); t.shadow = $('pfTextShadow').checked; markEdit(); requestRender(); } });
    $('pfTextBg').addEventListener('change', () => {
        const t = selectedText();
        if (!t) return;
        pushUndo();
        t.bg = $('pfTextBg').checked ? $('pfTextBgColor').value : null;
        markEdit(); requestRender();
    });
    $('pfTextBgColor').addEventListener('input', () => {
        const t = selectedText();
        if (t && $('pfTextBg').checked) { pushUndo(); t.bg = $('pfTextBgColor').value; markEdit(); requestRender(); }
    });
    $('pfAlignRow').addEventListener('click', (e) => {
        const b = e.target.closest('[data-align]');
        const t = selectedText();
        if (!b || !t) return;
        pushUndo();
        t.align = b.dataset.align;
        markEdit();
        syncTextPanel();
        requestRender();
    });
}

/** Slider → doc, with a live readout and one undo step per gesture. */
function bindRange(id, valId, apply, fmt, after) {
    const el = $(id);
    if (!el) return;
    const paint = () => { if (valId && $(valId) && fmt) $(valId).textContent = fmt(parseFloat(el.value)); };
    el.addEventListener('input', () => {
        pushUndo();
        apply(parseFloat(el.value));
        paint();
        markEdit(true);
        if (after) after(); else requestRender();
    });
    el.addEventListener('change', () => draftTouch(true));   // let go — one step of the card
    paint();
}

/** EyeDropper is Chromium-only; where it is missing the button never appears,
 *  and the colour input beside it does the whole job. */
function bindEyedropper(id, apply) {
    const b = $(id);
    if (!b) return;
    if (!('EyeDropper' in window)) return;
    b.hidden = false;
    b.addEventListener('click', async () => {
        try {
            const r = await new window.EyeDropper().open();
            if (r && r.sRGBHex) { apply(r.sRGBHex); funnel('eyedropper', {}); }
        } catch (_) { /* user pressed Escape */ }
    });
}

function buildFontGrid() {
    const grid = $('pfFontGrid');
    for (const f of fontsForLocale(LOCALE)) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pf-font';
        b.dataset.font = f.id;
        const sample = document.createElement('span');
        sample.className = 'pf-font-sample';
        sample.textContent = T('ui.font_sample');
        if (f.system) sample.style.fontFamily = f.css;
        const name = document.createElement('span');
        name.className = 'pf-font-name';
        name.textContent = T(`font.${f.id}`);
        b.append(sample, name);
        grid.appendChild(b);
    }
}

function syncFontGrid() {
    const t = selectedText();
    document.querySelectorAll('#pfFontGrid [data-font]').forEach((b) =>
        b.classList.toggle('is-on', !!t && b.dataset.font === t.font));
}

function syncTextPanel() {
    const t = selectedText();
    const has = !!t;
    $('pfTextEditor').hidden = !has;
    $('pfTextEmpty').hidden = has;
    syncTextChips();
    if (!t) return;
    $('pfTextInput').value = t.value;
    setRange('pfTextSize', 'pfTextSizeVal', Math.round(t.size * 1000), (v) => `${Math.round(v / 10)}`);
    $('pfTextColor').value = t.color;
    setRange('pfStrokeW', 'pfStrokeVal', Math.round(t.stroke * 100), (v) => `${Math.round(v)}`);
    $('pfStrokeColor').value = t.strokeColor;
    $('pfTextShadow').checked = !!t.shadow;
    $('pfTextBg').checked = !!t.bg;
    if (t.bg) $('pfTextBgColor').value = t.bg;
    setRange('pfTextRot', 'pfTextRotVal', t.rot, (v) => `${Math.round(v)}°`);
    setRange('pfTextCurve', 'pfTextCurveVal', t.curve || 0, (v) => `${Math.round(v)}`);
    document.querySelectorAll('#pfAlignRow [data-align]').forEach((b) =>
        b.classList.toggle('is-on', b.dataset.align === (t.align || 'center')));
    syncFontGrid();
}

function setRange(id, valId, value, fmt) {
    const el = $(id);
    if (!el) return;
    el.value = value;
    if (valId && $(valId) && fmt) $(valId).textContent = fmt(parseFloat(el.value));
}

function syncTextChips() {
    const wrap = $('pfTextList');
    wrap.textContent = '';
    doc.texts.forEach((t, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pf-chip' + (selection && selection.kind === 'text' && selection.id === t.id ? ' is-on' : '');
        const label = String(t.value || '').replace(/\s+/g, ' ').trim();
        b.textContent = label ? (label.length > 12 ? label.slice(0, 12) + '…' : label) : `${i + 1}`;
        b.addEventListener('click', () => {
            selection = { kind: 'text', id: t.id };
            syncTextPanel();
            syncCtxBar();
            requestRender();
        });
        wrap.appendChild(b);
    });
}

/* ─── shared layer actions ────────────────────────────────────────────────── */

function selectedObj() {
    if (!selection) return null;
    return selection.kind === 'text' ? selectedText() : selectedSticker();
}

function removeSelected() {
    const obj = selectedObj();
    if (!obj) return;
    pushUndo();
    if (selection.kind === 'text') {
        doc.texts = doc.texts.filter((x) => x.id !== obj.id);
        selection = doc.texts.length ? { kind: 'text', id: doc.texts[doc.texts.length - 1].id } : null;
    } else {
        doc.stickers = doc.stickers.filter((x) => x.id !== obj.id);
        selection = null;
    }
    markEdit();
    syncTextPanel(); syncStickerControls(); syncCtxBar();
    requestRender();
}

function duplicateSelected() {
    const obj = selectedObj();
    if (!obj) return;
    pushUndo();
    if (selection.kind === 'text') {
        // newText mints the id; spreading the source AFTER it would copy the
        // original's id and make two layers share one identity.
        const { id, ...rest } = obj;
        const made = newText(obj.value, {
            ...rest,
            x: Math.min(0.95, obj.x + 0.04),
            y: Math.min(0.95, obj.y + 0.05),
        });
        doc.texts.push(made);
        selection = { kind: 'text', id: made.id };
    } else {
        const made = newSticker(obj.char, { size: obj.size, rot: obj.rot, x: Math.min(0.95, obj.x + 0.05), y: Math.min(0.95, obj.y + 0.05) });
        doc.stickers.push(made);
        selection = { kind: 'sticker', id: made.id };
    }
    markEdit();
    syncTextPanel(); syncStickerControls(); syncCtxBar();
    requestRender();
}

function syncPanelsFromDoc() {
    syncAspectPill();
    syncPackUi();
    syncTextPanel();
    syncPhotoPanel();
    syncStickerControls();
    syncCtxBar();
    setRange('pfFrameW', 'pfFrameWVal', Math.round((doc.frameW || 1) * 100), (v) => `${Math.round(v)}%`);
    setRange('pfWinR', 'pfWinRVal', doc.winR == null ? -1 : Math.round(doc.winR * 100),
        (v) => (v < 0 ? T('ui.corner_frame') : `${Math.round(v)}%`));
    setRange('pfCapBand', 'pfCapBandVal', Math.round((doc.capBand || 0) * 100),
        (v) => (v > 0 ? `${Math.round(v)}%` : T('ui.cap_none')));
    updateUndoButtons();
}

/* ─── contextual bar (how the photo sits in the frame) ────────────────────── */

function slot() { return doc.photos[activeSlot] || doc.photos[0]; }

function bindCtxBar() {
    $('pfFitSeg').addEventListener('click', (e) => {
        const b = e.target.closest('[data-fit]');
        if (!b) return;
        pushUndo();
        const s = slot();
        s.fit = b.dataset.fit;
        // Switching to «целиком» with the photo still panned would show the
        // filler off-centre for no reason the user asked for.
        if (s.fit === 'fit') { s.pan.x = 0; s.pan.y = 0; }
        markEdit();
        syncCtxBar();
        syncPhotoPanel();
        requestRender();
        funnel('fit_mode', { m: s.fit });
    });
    bindRange('pfZoom', null, (v) => { slot().zoom = 1 + v / 100; });
    $('pfRot90').addEventListener('click', () => {
        pushUndo();
        slot().rot90 = (slot().rot90 + 90) % 360;
        markEdit();
        layoutCanvas();
    });
    $('pfFlip').addEventListener('click', () => { pushUndo(); slot().flip = !slot().flip; markEdit(); requestRender(); });
    $('pfCenter').addEventListener('click', () => {
        pushUndo();
        const s = slot();
        s.pan.x = 0; s.pan.y = 0; s.zoom = 1; s.rot = 0;
        markEdit();
        syncPhotoPanel();
        syncCtxBar();
        requestRender();
    });
}

function syncCtxBar() {
    const s = slot();
    document.querySelectorAll('#pfFitSeg [data-fit]').forEach((b) =>
        b.classList.toggle('is-on', b.dataset.fit === (s.fit || 'fill')));
    $('pfZoom').value = Math.round((s.zoom - 1) * 100);
    const note = $('pfSelNote');
    if (!selection) { note.hidden = true; note.textContent = ''; }
    else {
        note.hidden = false;
        note.textContent = selection.kind === 'text' ? T('ui.sel_text') : T('ui.sel_sticker');
    }
    $('pfBgRow').hidden = (s.fit || 'fill') !== 'fit';
}

/* ─── photo panel (light and colour) ──────────────────────────────────────── */

function bindPhotoPanel() {
    bindRange('pfRotFree', 'pfRotFreeVal', (v) => { slot().rot = v; }, (v) => `${v}°`);
    bindRange('pfBr', 'pfBrVal', (v) => { slot().adjust.br = v; }, pct);
    bindRange('pfCt', 'pfCtVal', (v) => { slot().adjust.ct = v; }, pct);
    bindRange('pfSat', 'pfSatVal', (v) => { slot().adjust.sat = v; }, pct);
    bindRange('pfWarm', 'pfWarmVal', (v) => { slot().adjust.warm = v; }, signed);
    bindRange('pfVig', 'pfVigVal', (v) => { slot().adjust.vig = v; }, (v) => `${Math.round(v)}`);
    $('pfPhotoPresets').addEventListener('click', (e) => {
        const b = e.target.closest('[data-preset]');
        if (!b) return;
        pushUndo();
        const a = slot().adjust;
        Object.assign(a, { br: 100, ct: 100, sat: 100, warm: 0, gray: 0, sepia: 0, vig: a.vig });
        switch (b.dataset.preset) {
            case 'bw': a.gray = 100; a.ct = 108; break;
            case 'sepia': a.sepia = 85; a.br = 103; break;
            case 'warm': a.warm = 55; break;
            case 'cool': a.warm = -45; a.sat = 106; break;
            case 'bright': a.br = 112; a.sat = 112; break;
            case 'orig': a.vig = 0; break;
        }
        document.querySelectorAll('#pfPhotoPresets [data-preset]').forEach((x) => x.classList.toggle('is-on', x === b));
        markEdit();
        syncPhotoPanel();
        requestRender();
        funnel('photo_preset', { p: b.dataset.preset });
    });
    $('pfBgSeg').addEventListener('click', (e) => {
        const b = e.target.closest('[data-bg]');
        if (!b) return;
        pushUndo();
        doc.bg = b.dataset.bg === 'color' ? $('pfBgColor').value : b.dataset.bg;
        $('pfBgColor').hidden = b.dataset.bg !== 'color';
        document.querySelectorAll('#pfBgSeg [data-bg]').forEach((x) => x.classList.toggle('is-on', x === b));
        markEdit();
        requestRender();
        funnel('backdrop', { b: b.dataset.bg });
    });
    $('pfBgColor').addEventListener('input', () => { pushUndo(); doc.bg = $('pfBgColor').value; markEdit(); requestRender(); });
    $('pfResetPhoto').addEventListener('click', () => {
        pushUndo();
        const keep = assets.photos[activeSlot];
        doc.photos[activeSlot] = newSlot();
        // In a pack the slot on the card IS the photo's own: the new one becomes it.
        if (pack && curItem()) curItem().slot = doc.photos[0];
        markEdit();
        syncPhotoPanel();
        syncCtxBar();
        if (keep) layoutCanvas();
    });
}

function pct(v) { return `${Math.round(v)}%`; }
function signed(v) { return (v > 0 ? '+' : '') + Math.round(v); }

function syncPhotoPanel() {
    const s = slot();
    setRange('pfRotFree', 'pfRotFreeVal', s.rot, (v) => `${v}°`);
    setRange('pfBr', 'pfBrVal', s.adjust.br, pct);
    setRange('pfCt', 'pfCtVal', s.adjust.ct, pct);
    setRange('pfSat', 'pfSatVal', s.adjust.sat, pct);
    setRange('pfWarm', 'pfWarmVal', s.adjust.warm, signed);
    setRange('pfVig', 'pfVigVal', s.adjust.vig, (v) => `${Math.round(v)}`);
    const mode = typeof doc.bg === 'string' && doc.bg !== 'blur' && doc.bg !== 'paper' ? 'color' : doc.bg;
    document.querySelectorAll('#pfBgSeg [data-bg]').forEach((b) => b.classList.toggle('is-on', b.dataset.bg === mode));
    $('pfBgColor').hidden = mode !== 'color';
}

/* ─── stickers ────────────────────────────────────────────────────────────── */

let emojiData = null;
let emojiLabels = null;
let stickerCat = 0;
const STICKER_GROUPS = [0, 1, 3, 4, 5, 6, 7, 8];

async function ensureStickers() {
    if (emojiData) return;
    try {
        const [base, i18n] = await Promise.all([
            fetch(CFG.emojiBase || '/data/emoji/base.json').then((r) => r.json()),
            fetch(CFG.emojiI18n || `/data/emoji/i18n-${LOCALE}.json`).then((r) => r.json()).catch(() => null),
        ]);
        // Keep to broadly-supported emoji: a sticker that renders as tofu on
        // the recipient's phone defeats the whole card.
        emojiData = (base.emojis || []).filter((e) => e.v <= 14 && STICKER_GROUPS.includes(e.g));
        emojiLabels = (i18n && i18n.labels) || {};
        buildStickerCats();
        renderStickerGrid();
    } catch (_) {
        $('pfStickerGrid').textContent = T('err.stickers');
    }
}

function buildStickerCats() {
    const wrap = $('pfStickerCats');
    wrap.textContent = '';
    STICKER_GROUPS.forEach((gid, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pf-chip' + (i === 0 ? ' is-on' : '');
        b.dataset.g = gid;
        b.textContent = T(`stg.${gid}`);
        b.addEventListener('click', () => {
            stickerCat = gid;
            $('pfStickerSearch').value = '';
            wrap.querySelectorAll('.pf-chip').forEach((x) => x.classList.toggle('is-on', x === b));
            renderStickerGrid();
        });
        wrap.appendChild(b);
    });
    let debounce = null;
    $('pfStickerSearch').addEventListener('input', () => {
        clearTimeout(debounce);
        debounce = setTimeout(renderStickerGrid, 160);
    });
}

function renderStickerGrid() {
    const grid = $('pfStickerGrid');
    grid.textContent = '';
    const q = $('pfStickerSearch').value.trim().toLowerCase();
    let list;
    if (q) {
        list = emojiData.filter((e) => {
            const loc = emojiLabels[e.h];
            return (loc && loc.toLowerCase().includes(q)) || e.n.toLowerCase().includes(q);
        }).slice(0, 120);
    } else {
        list = emojiData.filter((e) => e.g === stickerCat).slice(0, 160);
    }
    const frag = document.createDocumentFragment();
    for (const e of list) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pf-sticker';
        b.textContent = e.e;
        b.title = emojiLabels[e.h] || e.n;
        b.addEventListener('click', () => addSticker(e.e));
        frag.appendChild(b);
    }
    grid.appendChild(frag);
}

function addSticker(char) {
    pushUndo();
    const st = newSticker(char, { x: 0.5, y: 0.5 });
    doc.stickers.push(st);
    selection = { kind: 'sticker', id: st.id };
    markEdit();
    syncStickerControls();
    syncCtxBar();
    requestRender();
    funnel('sticker_add', { n: doc.stickers.length });
}

function selectedSticker() {
    if (!selection || selection.kind !== 'sticker') return null;
    return doc.stickers.find((s) => s.id === selection.id) || null;
}

function bindStickerPanel() {
    bindRange('pfStickerSize', 'pfStickerSizeVal', (v) => { const s = selectedSticker(); if (s) s.size = v / 1000; }, (v) => `${Math.round(v / 10)}`);
    bindRange('pfStickerRot', 'pfStickerRotVal', (v) => { const s = selectedSticker(); if (s) s.rot = v; }, (v) => `${Math.round(v)}°`);
    $('pfStickerDel').addEventListener('click', () => removeSelected());
    $('pfStickerDup').addEventListener('click', () => duplicateSelected());
}

function syncStickerControls() {
    const s = selectedSticker();
    $('pfStickerCtl').hidden = !s;
    if (s) {
        setRange('pfStickerSize', 'pfStickerSizeVal', Math.round(s.size * 1000), (v) => `${Math.round(v / 10)}`);
        setRange('pfStickerRot', 'pfStickerRotVal', s.rot, (v) => `${Math.round(v)}°`);
    }
}

/* ─── canvas gestures ─────────────────────────────────────────────────────── */

function canvasPoint(ev) {
    const r = cv.getBoundingClientRect();
    return {
        x: (ev.clientX - r.left) / r.width * previewW,
        y: (ev.clientY - r.top) / r.height * previewH,
    };
}

function bindCanvasGestures() {
    const pointers = new Map();
    let drag = null;            // {mode:'obj'|'pan'|'pinch'|'resize', ...}

    cv.style.touchAction = 'none';

    cv.addEventListener('pointerdown', (ev) => {
        // Capture is an enhancement; WebKit can throw on synthetic pointers,
        // and an exception here would kill the whole drag before it starts.
        try { cv.setPointerCapture(ev.pointerId); } catch (_) {}
        pointers.set(ev.pointerId, canvasPoint(ev));
        if (pointers.size === 2) {
            const [a, b] = [...pointers.values()];
            drag = { mode: 'pinch', d0: Math.hypot(a.x - b.x, a.y - b.y), zoom0: slot().zoom };
            return;
        }
        const p = canvasPoint(ev);
        const h = selectionHandle(ctx, previewW, previewH, doc, selection);
        // Remove wins over resize wins over everything under them.
        if (h && h.del && Math.hypot(p.x - h.del.x, p.y - h.del.y) <= h.del.r * 1.8) {
            removeSelected();
            return;
        }
        if (h && Math.hypot(p.x - h.x, p.y - h.y) <= h.r * 1.8) {
            pushUndo();
            const cx = h.obj.x * previewW, cy = h.obj.y * previewH;
            drag = {
                mode: 'resize', obj: h.obj,
                startDist: Math.max(8, Math.hypot(p.x - cx, p.y - cy)),
                startSize: h.obj.size,
                min: selection.kind === 'text' ? 0.02 : 0.04,
                max: selection.kind === 'text' ? 0.28 : 0.55,
            };
            return;
        }
        const hit = hitTest(ctx, previewW, previewH, doc, p.x, p.y);
        pushUndo();
        if (hit) {
            selection = { kind: hit.kind, id: hit.id };
            drag = { mode: 'obj', obj: hit.obj, sx: p.x, sy: p.y, ox: hit.obj.x, oy: hit.obj.y, tapKind: hit.kind };
            if (hit.kind === 'text') syncTextPanel(); else syncStickerControls();
        } else {
            // Which slot did the touch land in? Drag pans that photo.
            const { windows } = photoWindows(doc, previewW, previewH);
            for (let i = 0; i < windows.length; i++) {
                const w = windows[i];
                if (p.x >= w.x && p.x <= w.x + w.w && p.y >= w.y && p.y <= w.y + w.h) { activeSlot = i; break; }
            }
            const s = slot();
            if (selection) { selection = null; syncTextPanel(); syncStickerControls(); }
            drag = { mode: 'pan', sx: p.x, sy: p.y, px: s.pan.x, py: s.pan.y };
            syncPhotoPanel();
            syncLayoutSeg();
        }
        syncCtxBar();
        requestRender();
    });

    cv.addEventListener('pointermove', (ev) => {
        if (!pointers.has(ev.pointerId)) return;
        pointers.set(ev.pointerId, canvasPoint(ev));
        if (!drag) return;
        if (drag.mode === 'pinch' && pointers.size === 2) {
            const [a, b] = [...pointers.values()];
            const d = Math.hypot(a.x - b.x, a.y - b.y);
            slot().zoom = Math.min(5, Math.max(1, drag.zoom0 * (d / drag.d0)));
            drag.moved = true;
            markEdit(true);
            syncCtxBar();
            requestRender();
            return;
        }
        const p = canvasPoint(ev);
        if (drag.mode === 'resize') {
            const cx = drag.obj.x * previewW, cy = drag.obj.y * previewH;
            const k = Math.hypot(p.x - cx, p.y - cy) / drag.startDist;
            drag.obj.size = Math.min(drag.max, Math.max(drag.min, drag.startSize * k));
            drag.moved = true;
            markEdit(true);
            requestRender();
            return;
        }
        if (drag.mode === 'obj') {
            drag.obj.x = Math.min(1.05, Math.max(-0.05, drag.ox + (p.x - drag.sx) / previewW));
            drag.obj.y = Math.min(1.05, Math.max(-0.05, drag.oy + (p.y - drag.sy) / previewH));
            drag.moved = true;
            markEdit(true);
            requestRender();
        } else if (drag.mode === 'pan') {
            const s = slot();
            const { windows } = photoWindows(doc, previewW, previewH);
            const w = windows[Math.min(activeSlot, windows.length - 1)];
            // Convert px drag into pan fractions of the current slack.
            const ph = assets.photos[Math.min(activeSlot, windows.length - 1)];
            if (!ph) return;
            s.pan.x = clampPan(drag.px - (p.x - drag.sx) / Math.max(1, slackFor(s, ph, w).x));
            s.pan.y = clampPan(drag.py - (p.y - drag.sy) / Math.max(1, slackFor(s, ph, w).y));
            drag.moved = true;
            markEdit(true);
            requestRender();
        }
    });

    const up = (ev) => {
        pointers.delete(ev.pointerId);
        // The gesture is over: what it moved is one step of the card now (the
        // moves themselves were saved as they went, uncounted).
        if (drag && drag.moved && (!pointers.size || drag.mode === 'pinch')) {
            drag.moved = false;
            draftTouch(true);
        }
        // Reveal the layer's panel on a TAP, never on press.
        //
        // ⚠️ This ran on pointerdown until 2026-08-26 and it broke dragging on
        // a phone: switching the panel reflows the column, the canvas moves out
        // from under the finger mid-gesture, and every later move is measured
        // against a rect that is no longer where the drag started. The caption
        // came back to exactly the y it left (caught by the WebKit suite).
        if (drag && drag.mode === 'obj' && drag.tapKind) {
            const p = canvasPoint(ev);
            const moved = Math.hypot(p.x - drag.sx, p.y - drag.sy);
            if (moved < Math.max(6, previewW * 0.012)) {
                openTab(drag.tapKind === 'text' ? 'text' : 'stickers');
            }
        }
        if (drag && drag.mode === 'resize') {
            // Sliders mirror the canvas gesture once it settles.
            if (selection && selection.kind === 'text') syncTextPanel(); else syncStickerControls();
        }
        if (pointers.size < 2 && drag && drag.mode === 'pinch') drag = null;
        if (!pointers.size) drag = null;
    };
    cv.addEventListener('pointerup', up);
    cv.addEventListener('pointercancel', up);

    cv.addEventListener('wheel', (ev) => {
        if (!assets.photos[0]) return;
        ev.preventDefault();
        pushUndo();
        const s = slot();
        s.zoom = Math.min(5, Math.max(1, s.zoom * (ev.deltaY > 0 ? 0.94 : 1.06)));
        markEdit();
        syncCtxBar();
        requestRender();
    }, { passive: false });

    cv.addEventListener('dblclick', () => {
        if (selection && selection.kind === 'text') {
            openTab('text');
            $('pfTextInput').focus();
            $('pfTextInput').select();
        }
    });
}

function clampPan(v) { return Math.min(1, Math.max(-1, v)); }

function slackFor(s, ph, w) {
    const rad = (s.rot || 0) * Math.PI / 180;
    const sw = (s.rot90 === 90 || s.rot90 === 270) ? ph.h : ph.w;
    const sh = (s.rot90 === 90 || s.rot90 === 270) ? ph.w : ph.h;
    const needW = w.w * Math.abs(Math.cos(rad)) + w.h * Math.abs(Math.sin(rad));
    const needH = w.w * Math.abs(Math.sin(rad)) + w.h * Math.abs(Math.cos(rad));
    const base = s.fit === 'fit' ? Math.min(w.w / sw, w.h / sh) : Math.max(needW / sw, needH / sh);
    const cover = base * (s.zoom || 1);
    return {
        x: Math.max(1, (sw * cover - w.w) / 2),
        y: Math.max(1, (sh * cover - w.h) / 2),
    };
}

function bindKeyboard() {
    document.addEventListener('keydown', (ev) => {
        const tag = (ev.target.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || ev.target.isContentEditable) return;
        const meta = ev.ctrlKey || ev.metaKey;
        const kl = window.__keyLetter ? window.__keyLetter(ev) : String(ev.key || '').toLowerCase();
        if (meta && kl === 'z' && !ev.shiftKey) { ev.preventDefault(); undo(); return; }
        if (meta && (kl === 'y' || (ev.shiftKey && kl === 'z'))) { ev.preventDefault(); redo(); return; }
        if (meta && kl === 'd' && selection) { ev.preventDefault(); duplicateSelected(); return; }
        if (!selection) return;
        const obj = selectedObj();
        if (!obj) return;
        const step = ev.shiftKey ? 0.03 : 0.006;
        let handled = true;
        switch (ev.key) {
            case 'ArrowLeft': obj.x -= step; break;
            case 'ArrowRight': obj.x += step; break;
            case 'ArrowUp': obj.y -= step; break;
            case 'ArrowDown': obj.y += step; break;
            case 'Delete': case 'Backspace': removeSelected(); return;
            case 'Escape': selection = null; syncTextPanel(); syncStickerControls(); syncCtxBar(); requestRender(); return;
            default: handled = false;
        }
        if (handled) { ev.preventDefault(); markEdit(); requestRender(); }
    });
}

/* ─── export ──────────────────────────────────────────────────────────────── */

function exportDims(d, a) {
    // Target: the photo window shows the photo at its NATIVE cropped pixels —
    // the full-resolution promise no competitor keeps. Bounded by the real
    // per-device canvas ceiling, probed, not guessed. `d`/`a` — the card of one
    // photo of a pack (docFor()); the card on the stage by default.
    const D = d || doc, A = a || assets;
    const ph = A.photos[0];
    if (!ph) return { w: 1080, h: 1080 };
    const ar = docAspect(D, A);
    const asp = ASPECTS[D.aspect];
    let w;
    if (asp && asp.printPx) {
        w = asp.printPx[0];
    } else {
        const probeW = 1000;
        const { windows } = photoWindows(D, probeW, Math.round(probeW / ar));
        const win = windows[0];
        const s = D.photos[0];
        const swap = s.rot90 === 90 || s.rot90 === 270;
        const sw = swap ? ph.h : ph.w;
        const sh = swap ? ph.w : ph.h;
        // Window size as a share of canvas WIDTH, on both axes.
        const fw = win.w / probeW, fh = win.h / probeW;
        // Solve scale(W)·zoom = 1 for the ratio the mode actually uses: cover
        // takes the larger of the two fits, contain the smaller. Using only
        // the width term (as this did before) under-sized every canvas whose
        // height was the binding side.
        const z = s.zoom || 1;
        w = (s.fit === 'fit')
            ? Math.max(sw / fw, sh / fh) / z
            : Math.min(sw / fw, sh / fh) / z;
        w = Math.max(1080, Math.round(w));
    }
    const sizeSel = $('pfSize').value;
    if (sizeSel !== 'max') w = Math.min(w, parseInt(sizeSel, 10));
    const limit = canvasLimits();
    let h = Math.round(w / ar);
    const maxSide = Math.min(limit.side, 8192);
    if (w > maxSide) { w = maxSide; h = Math.round(w / ar); }
    if (h > maxSide) { h = maxSide; w = Math.round(h * ar); }
    if (w * h > limit.area) {
        const k = Math.sqrt(limit.area / (w * h));
        w = Math.floor(w * k); h = Math.floor(h * k);
    }
    return { w, h };
}

/* A face still in flight does not hold the card for long: after this much
 * visible time the file is made with the fallback — what the preview shows —
 * and the person is told; the face keeps loading for the next press. */
const EXPORT_FONT_WAIT = 25000;

async function renderExport() {
    stage('exporting');
    const missedFonts = await window.__bounded(ensureDocFonts(doc, LOCALE), EXPORT_FONT_WAIT, 'fonts')
        .catch(() => missingDocFonts(doc));
    // The stage may still show a photo's small stand-in (a pack, or the photo a
    // pack shrank to): the file is made from the photo itself.
    await stageFull();
    const { w, h } = exportDims();
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const x = c.getContext('2d');
    render(x, w, h, doc, assets, { exporting: true });
    return { canvas: c, missedFonts };
}

function chosenFormat() {
    const v = $('pfFmt').value;
    if (v === 'auto') return (frameHasAlpha(doc.frame) || (doc.customFrame && !assets.photos[0])) ? 'png' : 'jpeg';
    return v;
}

/* The shared ladder (timbrica-core.js): toBlob → OffscreenCanvas → data URL,
 * each with a deadline — a toBlob an extension swallows no longer keeps the
 * export waiting for ever; no rung answering is CANVAS_ENCODE_FAILED. */
function toBlob(canvas, format, quality) {
    return window.__canvasBlob(canvas, mimeFor(format), quality);
}

/* What the person reads when the file could not be made. `why: 'null'` is the
 * browser refusing the size (memory) — a smaller export helps; silence is the
 * encoder not answering at all — usually an extension, and size is no cure. */
function exportFailureText(err) {
    if (err && err.code === 'CANVAS_ENCODE_FAILED' && err.why !== 'null') return T('err.encode_silent');
    return T('err.export');
}

function bindExportPanel() {
    // Offering a format the engine silently turns into PNG is a lie told by
    // the dropdown; drop the option instead of the promise.
    if (!formatSupported('webp')) {
        const opt = $('pfFmt').querySelector('option[value=webp]');
        if (opt) opt.remove();
    }
    $('pfFmt').value = settings.load('fmt', 'auto');
    if (!$('pfFmt').value) $('pfFmt').value = 'auto';
    $('pfQuality').value = settings.load('q', 92);
    $('pfSize').value = settings.load('size', 'max');
    $('pfQualityVal').textContent = $('pfQuality').value;
    const syncQ = () => { $('pfQualityRow').hidden = chosenFormat() === 'png'; };
    $('pfFmt').addEventListener('change', () => { settings.save('fmt', $('pfFmt').value); syncQ(); updateExpEstimate(); });
    $('pfQuality').addEventListener('input', () => {
        settings.save('q', $('pfQuality').value);
        $('pfQualityVal').textContent = $('pfQuality').value;
    });
    $('pfSize').addEventListener('change', () => { settings.save('size', $('pfSize').value); updateExpEstimate(); });
    syncQ();

    // In a pack the main button is the whole pack as one archive; the row under
    // it («Эта открытка») acts on the card on the stage.
    $('pfDownload').addEventListener('click', () => (pack ? runPackExport() : runExport('download')));
    $('pfDownloadOne').addEventListener('click', () => runExport('download'));
    $('pfPackCancel').addEventListener('click', () => { packCancel = true; });
    if (navigator.share && navigator.canShare) $('pfShare').hidden = false;
    $('pfShare').addEventListener('click', () => runExport('share'));
    $('pfCopy').addEventListener('click', () => runExport('copy'));
    $('pfPrint').addEventListener('click', () => runExport('print'));
    $('pfSaveTpl').addEventListener('click', saveMyTpl);
    // "Replace / remove the file" lives in the collapsed uploader row (its own
    // «Другой файл» + ×) — a second button here duplicated it and read as two
    // different actions.
}

/** Честная цифра до нажатия: что именно уедет в файл, и каким оно будет на бумаге. */
function updateExpEstimate() {
    const el = $('pfExpEstimate');
    if (!el) return;
    el.textContent = '';
    if (!assets.photos[0]) return;
    const d = exportDims();
    const fmt = formatSupported(chosenFormat()) ? chosenFormat() : 'png';
    const label = fmt === 'jpeg' ? 'JPG' : fmt.toUpperCase();
    const cm = (px) => (px / 300 * 2.54).toFixed(1).replace('.', ',');
    const facts = [
        `${T('ui.exp_estimate')} <b>${d.w}×${d.h}</b> px · <b>${label}</b>`,
        `${T('ui.exp_print')} <b>${cm(d.w)}×${cm(d.h)} ${T('ui.cm')}</b> ${T('ui.at_300dpi')}`,
    ];
    // In a pack the size above is the card on the stage; each file of the
    // archive gets its own photo's full size.
    if (pack) facts.push(`${T('ui.pack_files')} <b>${pack.items.length}</b>`);
    el.innerHTML = facts.map((f) => `<span>${f}</span>`).join('');
}

let exporting = false;

async function runExport(how) {
    if (exporting || !assets.photos[0]) return;
    exporting = true;
    const busyBtn = pack ? $('pfDownloadOne') : $('pfDownload');
    busyBtn.classList.add('is-busy');
    const t0 = performance.now();
    try {
        const { canvas, missedFonts } = await renderExport();
        const format = chosenFormat();
        const quality = Math.min(1, Math.max(0.5, parseInt($('pfQuality').value, 10) / 100));
        const fmt = formatSupported(format) ? format : 'png';
        const blob = await toBlob(canvas, fmt, fmt === 'png' ? undefined : quality);
        const name = outputName(fmt, pack ? curItem() : null);

        if (how === 'share') {
            const file = new File([blob], name, { type: blob.type });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                try {
                    await navigator.share({ files: [file] });
                    funnel('share_ok', {});
                } catch (err) {
                    if (err && err.name === 'AbortError') { funnel('share_cancel', {}); stage('ready'); return; }
                    // Some targets refuse files — fall back to a download so
                    // the press still produces the card.
                    deliver(blob, name);
                }
            } else {
                deliver(blob, name);
            }
        } else if (how === 'copy') {
            try {
                await copyToClipboard(blob);
                toast(T('msg.copied'), 'success');
                funnel('copy_ok', {});
            } catch (_) {
                toast(T('err.copy'), 'error');
                stage('ready');
                return;
            }
        } else if (how === 'print') {
            printSheet(blob);
            funnel('print', {});
        } else {
            deliver(blob, name);
        }

        dirty = false;
        // «Мои работы»: this card was delivered (downloaded, shared, copied or
        // printed) — it waits in the list as such and no longer reopens by itself.
        if (drafts) drafts.exported();
        try {
            window.__aeToolComplete && window.__aeToolComplete({
                how, fmt,
                frame: doc.frame,
                layout: doc.layout,
                fit: doc.photos[0].fit,
                texts: doc.texts.length,
                stickers: doc.stickers.length,
                custom: doc.customFrame ? 1 : 0,
                w: canvas.width, h: canvas.height,
                bytes: blob.size,
                ms: Math.round(performance.now() - t0),
            });
        } catch (_) {}
        announce(T('msg.exported'));
        if (missedFonts.length) toast(T('warn.font_fallback'), 'warning');
    } catch (err) {
        funnel('export_fail', { err: String(err && err.message || err).slice(0, 80) });
        // A failed export is an ending, not «exporting» for ever: the stage is
        // the tier-0 beacon that says what happened to people without consent.
        stage('error', { reason: (err && (err.code || err.name)) || 'export', why: (err && err.why) || '' });
        toast(exportFailureText(err), 'error');
    } finally {
        exporting = false;
        busyBtn.classList.remove('is-busy');
    }
}

/* ─── the pack as one archive ─────────────────────────────────────────────────
   Every photo is decoded at full size, drawn into its card by the same render()
   as everything else, encoded, handed to the archive and let go — so the pack
   never holds more than one full photo and one card at a time. The archive is
   the shared batch door (batch-deliver.js): it names entries apart, refuses an
   archive the browser could not hand over, and splits a pack past the zip
   ceiling into parts instead of losing it. */

let packCancel = false;

async function runPackExport() {
    if (exporting || !pack || packLoading()) return;
    const items = pack.items.filter((it) => it.state === 'ready');
    if (!items.length) return;
    // FIRST await of the click, as the door asks (its folder lane lives on the
    // click's activation). «Скачать все … в ZIP» promises an archive — no
    // folder dialog on some presses and not on others.
    const BD = window.__batchDeliver || (window.__ensureBatchDeliver ? await window.__ensureBatchDeliver().catch(() => null) : null);
    if (!BD) {
        // One file per gesture: a loop of downloads would lose all but the first
        // and stop the tab downloading at all. Refuse out loud instead.
        toast(T('err.export'), 'error');
        try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'batch_primitive_missing' }); } catch (_) {}
        return;
    }
    exporting = true;
    packCancel = false;
    const btn = $('pfDownload');
    btn.classList.add('is-busy');
    const t0 = performance.now();
    const n = items.length;
    const format = chosenFormat();
    const fmt = formatSupported(format) ? format : 'png';
    const quality = fmt === 'png' ? undefined : Math.min(1, Math.max(0.5, parseInt($('pfQuality').value, 10) / 100));
    // The archive is the pack as it was on the screen at the press: the design
    // and every placement are frozen here, so a caption edited while the cards
    // are being made does not land on half of them.
    const design = Object.assign(JSON.parse(designJson()), { _locale: doc._locale });
    const placed = new Map(items.map((it) => [it, cloneSlot(it.slot)]));
    const frame = assets.customFrame;
    let batch = null;
    try {
        batch = await BD.openBatch({
            slug: SLUG, archiveOnly: true, total: n, zipName: zipName(),
            onPack: (frac) => packProgress(n, n, frac),
        });
        stage('exporting');
        packProgress(0, n);
        const missedFonts = await window.__bounded(ensureDocFonts(design, LOCALE), EXPORT_FONT_WAIT, 'fonts')
            .catch(() => missingDocFonts(design));
        let bytes = 0;
        for (let i = 0; i < n; i++) {
            if (packCancel) break;
            const it = items[i];
            // A photo the person removed meanwhile is not in the archive.
            if (!pack || !pack.items.includes(it)) continue;
            const kept = fulls.get(it);
            const dec = kept || await decodeFile(it.file, {});
            if (packCancel) { if (!kept) freeDecoded(dec); break; }
            const d = Object.assign({}, design, { photos: [placed.get(it), design.photos[1]], layout: 'single' });
            const a = { photos: [dec, null], customFrame: frame };
            const { w, h } = exportDims(d, a);
            const c = document.createElement('canvas');
            c.width = w; c.height = h;
            render(c.getContext('2d'), w, h, d, a, { exporting: true });
            if (!kept) freeDecoded(dec);
            const blob = await toBlob(c, fmt, quality);
            c.width = 0; c.height = 0;               // the card's pixels go now, not at the next GC
            bytes += blob.size;
            await batch.write(outputName(fmt, it), blob);
            packProgress(i + 1, n);
            if (window.__yieldTick) await window.__yieldTick(0);
        }
        if (packCancel) {
            batch.abort();
            funnel('pack_cancel', { n });
            announce(T('msg.pack_cancelled'));
            stage('ready');
            return;
        }
        packProgress(n, n, 0);
        const out = await batch.finish();
        if (!out || out.via === 'refused' || out.via === 'empty') {
            // The door has already told the person why (too big, unreadable).
            stage('error', { reason: 'batch_refused', why: (out && out.via) || '' });
            return;
        }
        dirty = false;
        if (drafts) drafts.exported();
        try {
            window.__aeToolComplete && window.__aeToolComplete({
                how: 'zip', fmt, n, via: out.via, parts: out.parts || 0,
                frame: design.frame, aspect: design.aspect,
                texts: design.texts.length, stickers: design.stickers.length,
                custom: design.customFrame ? 1 : 0,
                touched: items.filter((it) => slotTouched(placed.get(it), packDefaults)).length,
                bytes, ms: Math.round(performance.now() - t0),
            });
        } catch (_) {}
        announce(T('msg.pack_exported').replace(':n', n));
        if (missedFonts.length) toast(T('warn.font_fallback'), 'warning');
    } catch (err) {
        try { if (batch) batch.abort(); } catch (_) {}
        funnel('export_fail', { pack: n, err: String((err && err.message) || err).slice(0, 80) });
        stage('error', { reason: (err && (err.code || err.name)) || 'export', why: (err && err.why) || '' });
        toast(exportFailureText(err), 'error');
    } finally {
        exporting = false;
        packCancel = false;
        btn.classList.remove('is-busy');
        packProgress(null);
    }
}

/* The progress row under the buttons: «Собираю 4 из 12», then the archive
   itself being packed. done = null hides it. */
function packProgress(done, total, packing) {
    const box = $('pfPackProg');
    if (done == null) { box.hidden = true; return; }
    box.hidden = false;
    const zipping = packing != null;
    const frac = zipping ? Math.max(0, Math.min(1, packing)) : done / Math.max(1, total);
    $('pfPackBar').style.width = `${Math.round(frac * 100)}%`;
    $('pfPackProgText').textContent = zipping ? T('ui.pack_zipping')
        : T('ui.pack_progress').replace(':i', Math.min(total, done + 1)).replace(':n', total);
    $('pfPackCancel').hidden = zipping;
}

/* The archive is named after the card's greeting, as a single card is. */
function zipName() {
    const first = (doc.texts.find((t) => String(t.value || '').trim()) || {}).value || '';
    const base = first.trim().split(/\r?\n/)[0].slice(0, 48).trim() || T('ui.pack_zip_base');
    try {
        if (window.__outputName) {
            const named = window.__outputName.join(base, 'zip');
            if (named) return named;
        }
    } catch (_) {}
    return `${base.replace(/[\\/:*?"<>|\r\n]+/g, ' ').trim().slice(0, 48) || 'photo-frame'}.zip`;
}

/** Name the file after what is ON it, not after the tool. A folder of
 *  photo-frame(1..9).jpg is unopenable a week later. */
function outputName(fmt, it) {
    const ext = fmt === 'jpeg' ? 'jpg' : fmt;
    // A card of a pack is named after ITS photo: twelve cards share one
    // greeting, and «IMG_0412.jpg» in the archive says which photo it is.
    if (it && it.name) {
        const own = String(it.name).replace(/\.[a-z0-9]{2,5}$/i, '').slice(0, 80).trim();
        try {
            if (own && window.__outputName) {
                const named = window.__outputName.join(own, ext);
                if (named) return named;
            }
        } catch (_) {}
        const safeOwn = own.replace(/[\\/:*?"<>|\r\n]+/g, ' ').trim().slice(0, 80);
        if (safeOwn) return `${safeOwn}.${ext}`;
    }
    // The FIRST caption only. Joining every layer produced names like
    // «С Днём рождения! Счастья, здоровья и любви!.jpg» — the greeting is the
    // title of the card, the wishes underneath are not part of its name.
    const first = (doc.texts.find((t) => String(t.value || '').trim()) || {}).value || '';
    const base = first.trim().split(/\r?\n/)[0].slice(0, 48).trim() || T('ui.file_base');
    // __outputName.join takes a STRING (it sanitises and byte-truncates itself)
    // and returns '' when nothing survives — a greeting made entirely of
    // characters Windows forbids is one emoji away, and an empty name would
    // reach __downloadFile as ".jpg".
    try {
        if (window.__outputName) {
            const named = window.__outputName.join(base, ext);
            if (named) return named;
        }
    } catch (_) {}
    const safe = base.replace(/[\\/:*?"<>|\r\n]+/g, ' ').trim().slice(0, 48);
    return `${safe || 'photo-frame'}.${ext}`;
}

function deliver(blob, name) {
    if (window.__downloadFile) window.__downloadFile(blob, name);
    else fallbackDownload(blob, name);
}

/**
 * Print the card, not the page.
 *
 * A plain window.print() here would put the editor on paper. The composed
 * image is dropped into a print-only sheet inside the tool's print root, and
 * the tool's own @media print rules hide the workspace for the duration.
 */
function printSheet(blob) {
    const holder = $('pfPrintSheet');
    if (!holder) { toast(T('err.export'), 'error'); return; }
    const url = URL.createObjectURL(blob);
    const img = holder.querySelector('img');
    const done = () => {
        setTimeout(() => {
            document.documentElement.classList.remove('pf-printing');
            URL.revokeObjectURL(url);
        }, 500);
    };
    img.onload = () => {
        document.documentElement.classList.add('pf-printing');
        window.addEventListener('afterprint', done, { once: true });
        // Some engines never fire afterprint; the class must not stick.
        setTimeout(done, 12000);
        window.print();
    };
    img.onerror = () => { URL.revokeObjectURL(url); toast(T('err.export'), 'error'); };
    img.src = url;
}

function fallbackDownload(blob, name) {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 4000);
}
